import { useEffect, useState, Component } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import InitiateGovDialog from "./InitiateGovDialog";
import { PageHero, Card, SectionTitle, PrimaryButton, GhostButton, C, Loader, StatusPill } from "@/components/district/kit";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { UsersThree, Buildings, ClipboardText, UploadSimple, DownloadSimple, Plus, Trash, CheckCircle, Image, IdentificationCard, CircleNotch, CalendarBlank, RocketLaunch, FolderOpen, PencilSimple, ArrowsLeftRight, Certificate, Scroll, Tag, UsersFour, Target, ChartBar, Invoice, Receipt, Bank, Handshake, HandHeart, BookOpen, Coins, CalendarCheck, FileText, CalendarPlus, Sliders, CaretDown, WarningCircle, UserPlus, ArrowClockwise, DotsThreeVertical, PaperPlaneTilt } from "@phosphor-icons/react";

// Three plain, self-explanatory groups. The group IS the behaviour — no per-item type pills.
const DOC_CATS = [
  { key: "upload", title: "Upload a document", hint: "Attach a file — image, PDF, Word or Excel. No typing needed.", Icon: FolderOpen, kinds: ["document", "template"], add: "document" },
  { key: "fill", title: "Fill in the app", hint: "The club enters the details directly in the app — no file.", Icon: PencilSimple, kinds: ["info"], add: "info" },
  { key: "mixed", title: "Either — fill in or upload", hint: "The club can type it in, or attach a file. Whichever is easier.", Icon: ArrowsLeftRight, kinds: ["both"], add: "both" },
];

// Specific icon per document (keyword match, order matters — attendance before board).
const DOC_ICON_RULES = [
  [/meeting|attendance/i, CalendarCheck],
  [/charter|certificate/i, Certificate],
  [/byelaw|bylaw/i, Scroll],
  [/classification/i, Tag],
  [/sub-?committee|committee/i, UsersFour],
  [/board/i, UsersThree],
  [/plan|objective/i, Target],
  [/budget/i, ChartBar],
  [/payment proof|payment/i, Receipt],
  [/invoice/i, Invoice],
  [/audited|account/i, Bank],
  [/rotaract|interact/i, Handshake],
  [/community corps|rcc/i, HandHeart],
  [/guide/i, BookOpen],
  [/trf|contribution/i, Coins],
];
const iconFor = (name = "") => (DOC_ICON_RULES.find(([re]) => re.test(name)) || [null, FileText])[1];

function FilePick({ testid, label, file, onPick }) {
  return (
    <label data-testid={testid} className="flex cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed px-4 py-3 text-[15px] transition-colors hover:bg-[#F7FAFF]" style={{ borderColor: C.line }}>
      <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: C.azureSoft, color: C.azure }}><UploadSimple size={18} weight="bold" /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium" style={{ color: C.ink }}>{label}</span>
        <span className="block truncate" style={{ color: C.sub }}>{file ? file.name : "Choose an Excel file (.xlsx)"}</span>
      </span>
      <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => onPick(e.target.files[0])} />
    </label>
  );
}

// A count value may arrive as a number OR as an object like {people, clubs, zones}.
// Never render the raw object as a React child (that throws and blanks the page).
const peopleOf = (n) => (n && typeof n === "object" ? Number(n.people || 0) : Number(n || 0));
const subOf = (n, k) => (n && typeof n === "object" ? Number(n[k] || 0) : 0);

// Guards the mapping-summary render so a bad/unexpected field can never blank the whole
// page. If a render error slips through, show a recoverable message instead of a deadend.
class SummaryErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null, key: props.resetKey }; }
  static getDerivedStateFromError(err) { return { err }; }
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.key) return { err: null, key: props.resetKey };
    return null;
  }
  render() {
    if (this.state.err) {
      return (
        <div className="mt-3 rounded-xl border p-3.5 text-[13.5px]" data-testid="setup-summary-error"
          style={{ borderColor: "#F0C7C7", background: "#FDECEC", color: C.cranberry }}>
          <b>We couldn't display the mapping preview.</b> Your upload was validated but a detail
          in the summary couldn't be shown. Nothing was saved. Please re-run “Preview &amp; validate”,
          or download the unmatched report — if it keeps happening, re-check your workbook.
        </div>
      );
    }
    return this.props.children;
  }
}


function Summary({ data }) {
  if (!data) return null;
  const entries = Object.entries(data).filter(([, v]) => typeof v === "number" || typeof v === "string");
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="preview-summary">
      {entries.map(([k, v]) => (
        <span key={k} className="rounded-lg px-3 py-1.5 text-[14px] font-medium" style={{ background: "#F2F6FC", color: C.ink }}>
          <b>{v}</b> {k.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

// 1) Member repository -----------------------------------------------------
function UploadStatus({ kind }) {
  const [info, setInfo] = useState(undefined);
  const [owner, setOwner] = useState(false);
  const [did, setDid] = useState(null);
  useEffect(() => {
    api.get("/me/principal").then(async (r) => {
      setOwner(!!r.data.is_platform_owner); setDid(r.data.district_id);
      if (!r.data.district_id) { setInfo(null); return; }
      try {
        const u = await api.get(`/support/districts/${r.data.district_id}/uploads`);
        setInfo(u.data.uploads?.[kind] || null);
      } catch { setInfo(null); }
    }).catch(() => setInfo(null));
  }, [kind]);
  if (!info) return null;
  const dt = info.uploaded_at ? new Date(info.uploaded_at).toLocaleString() : "";
  const download = async () => {
    try { const r = await api.get(`/support/districts/${did}/uploads/${kind}/download`); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Download unavailable"); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-[12.5px]" style={{ background: "#F1FAF4", color: "#1E7A46" }} data-testid={`upload-status-${kind}`}>
      <CheckCircle size={15} weight="fill" />
      <span>Uploaded: <b>{info.filename || "file"}</b>{dt && <span style={{ color: C.sub }}> · {dt}</span>}</span>
      {owner && <button data-testid={`upload-download-${kind}`} onClick={download} className="ml-auto inline-flex items-center gap-1 font-semibold" style={{ color: C.azure }}><DownloadSimple size={13} weight="bold" /> Download</button>}
    </div>
  );
}

function MembersSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/district-members/preview", fd);
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the file"); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try {
      await api.post("/district-members/commit", { batch_id: preview.batch_id });
      toast.success("Member repository updated"); setDone(true); setPreview(null); setFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setBusy(false);
  };
  const errors = (preview?.results || []).filter((r) => r.action === "error");

  return (
    <Card className="border-2" style={{ borderColor: "#F5A524" }}>
      <SectionTitle icon={UsersThree} title="Member repository"
        sub="Upload your cleaned member sheet (Club, Member ID, Name, Gender, Email, Phone, Member Admit Date, …). Gender fills the membership infographic; 'Member Admit Date' shows who joined this Rotary Year. The full RI export still works too."
        right={done && <StatusPill status="ok">Updated</StatusPill>} />
      <button data-testid="members-template-btn" onClick={() => window.open(`${api.defaults.baseURL}/district-members/template`, "_blank")}
        className="mb-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}><DownloadSimple size={14} weight="bold" /> Download sample sheet</button>
      <FilePick testid="members-file" label="Member sheet (.xlsx)" file={file} onPick={(f) => { setFile(f); setPreview(null); }} />
      <UploadStatus kind="members" />
      <p className="mt-2 text-[13px]" style={{ color: C.sub }}>Re-uploading updates matching members. Members left out of the file are never deleted.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <PrimaryButton data-testid="members-preview-btn" onClick={runPreview} disabled={!file || busy}>Preview changes</PrimaryButton>
        {preview && errors.length === 0 && <GhostButton data-testid="members-commit-btn" onClick={commit} disabled={busy}>Apply update</GhostButton>}
      </div>
      {busy && <div className="mt-3"><Loader label="Reading workbook…" /></div>}
      {preview && (
        <>
          <Summary data={preview.summary} />
          {errors.length > 0 && (
            <div className="mt-3 rounded-xl p-3 text-[14px]" style={{ background: "#FDECEC", color: C.cranberry }} data-testid="members-errors">
              <b>{errors.length} row(s) need attention.</b> Nothing was saved. Fix these and preview again.
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function FoundationGivingSection() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get("/foundation-giving/status").then((r) => setStatus(r.data && r.data.filename ? r.data : null)).catch(() => {}); }, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/foundation-giving/upload", fd);
      setResult(r.data);
      setStatus({ filename: file.name, matched: r.data.matched, unmatched: r.data.unmatched, district_total: r.data.district_total, uploaded_at: new Date().toISOString() });
      toast.success(`Giving imported — ${r.data.matched} club(s) matched`);
      setFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not import the workbook"); }
    setBusy(false);
  };

  return (
    <Card className="border-2" style={{ borderColor: "#E08600" }} data-testid="foundation-giving-section">
      <SectionTitle icon={HandHeart} title="Total giving"
        sub="Upload the club-wise giving workbook (tabs: 'Category Subtotals' for per-club giving by the 4 categories, and 'Giving Bands' for the donor distribution). Feeds the Total Giving and Donors-by-Giving-Band insights. Figures are total giving to date."
        right={status && <StatusPill status="ok">Loaded</StatusPill>} />
      <FilePick testid="giving-file" label="Giving workbook (.xlsx)" file={file} onPick={(f) => { setFile(f); setResult(null); }} />
      {status && !result && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-[12.5px]" style={{ background: "#FFF7ED", color: "#9A5B00" }} data-testid="giving-current-status">
          <CheckCircle size={15} weight="fill" />
          <span>Loaded: <b>{status.filename}</b> · {status.matched} club(s) · ${Math.round(status.district_total || 0).toLocaleString()}</span>
        </div>
      )}
      <div className="mt-4">
        <PrimaryButton data-testid="giving-upload-btn" onClick={upload} disabled={!file || busy}>Import giving data</PrimaryButton>
      </div>
      {busy && <div className="mt-3"><Loader label="Reading workbook…" /></div>}
      {result && (
        <div className="mt-3 space-y-2" data-testid="giving-result">
          <div className="flex flex-wrap gap-2 text-[13px] font-semibold">
            <span className="rounded-lg px-3 py-1.5" style={{ background: "#F1FAF4", color: "#1E7A46" }}>{result.matched} matched</span>
            {result.unmatched > 0 && <span className="rounded-lg px-3 py-1.5" style={{ background: "#FDECEC", color: C.cranberry }}>{result.unmatched} unmatched</span>}
            <span className="rounded-lg px-3 py-1.5" style={{ background: C.azureSoft, color: C.azure }}>${Math.round(result.district_total || 0).toLocaleString()} total</span>
          </div>
          {result.unmatched > 0 && (
            <div className="rounded-xl p-3 text-[13px]" style={{ background: "#FDECEC", color: C.cranberry }} data-testid="giving-unmatched">
              <b>{result.unmatched} club name(s) didn't match</b> and were skipped (rename in the sheet to match your club list, then re-upload):
              <p className="mt-1 font-medium" style={{ color: C.sub }}>{(result.unmatched_clubs || []).join(", ")}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// 2) Clubs & allocations ---------------------------------------------------
// Roles are no longer defined up-front. They are DISCOVERED from the uploaded/pasted sheets
// during "Preview & validate" and then configured (granularity + access) in the post-preview
// "Configure detected roles" panel below — see ClubsSection.

// Paste-and-map importer: an alternative to file upload. Admin pastes rows copied from a
// spreadsheet; we detect columns and let them map each column to an app field (auto-guessed).
// No fixed header names required — the mapping IS the naming rule, on screen.
const OFFICER_FIELDS = ["Name", "Position", "Member ID", "Phone", "Email"];
const _norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function parseTable(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 1) return null;
  const delim = lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(delim).map((h) => h.trim());
  const rows = lines.slice(1).map((l) => l.split(delim).map((c) => c.trim()));
  return { headers, rows };
}
function autoMap(headers, fields) {
  return headers.map((h) => fields.find((f) => _norm(f) === _norm(h)) || "Ignore");
}
function buildRows(parsed, map) {
  if (!parsed) return [];
  return parsed.rows.map((row) => {
    const o = {};
    map.forEach((f, i) => { if (f && f !== "Ignore") o[f] = row[i] || ""; });
    return o;
  }).filter((o) => Object.values(o).some((v) => v));
}


// Compact "Upload" button (wraps a hidden file input) — sits inline next to Paste/Sample.
function UploadButton({ testid, onPick, label = "Upload" }) {
  return (
    <label data-testid={testid} className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold text-white" style={{ background: C.indigo }}>
      <UploadSimple size={14} weight="bold" /> {label}
      <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
    </label>
  );
}

// Paste + map dialog (spreadsheet-style grid). The column mapping IS the naming rule, on screen.
function PasteDialog({ open, onClose, kind, fields, onSave }) {
  const [text, setText] = useState("");
  const [map, setMap] = useState([]);
  const parsed = parseTable(text);
  useEffect(() => { if (parsed) setMap(autoMap(parsed.headers, fields)); /* eslint-disable-next-line */ }, [text, fields.length]);
  const save = () => {
    const rows = buildRows(parsed, map);
    if (!rows.length) { toast.error("Paste some rows (with a header row) first"); return; }
    onSave(rows, text);
  };
  const title = kind === "officers" ? "Paste district officers" : "Paste club allocation";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl" data-testid={`paste-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Copy the rows from your spreadsheet (including the header row) and paste below. We detect the
            columns — set each one to the matching field. Unmapped columns are ignored.
          </DialogDescription>
        </DialogHeader>
        <textarea data-testid={`paste-textarea-${kind}`} value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder={kind === "officers" ? "Name  Position  Member ID  Phone  Email" : "Club Name  Zone  Assistant Governor Member ID  Assistant Governor Phone  President Member ID  President Phone  …"}
          className="w-full rounded-lg border p-2 text-[12.5px] font-mono" style={{ borderColor: C.line }} />
        {parsed && (
          <div className="max-h-[46vh] overflow-auto rounded-lg border" style={{ borderColor: C.line }} data-testid={`paste-grid-${kind}`}>
            <table className="w-full border-collapse text-[12.5px]">
              <thead className="sticky top-0" style={{ background: "#F1ECFF" }}>
                <tr>
                  {parsed.headers.map((h, i) => (
                    <th key={i} className="border p-1 text-left align-top" style={{ borderColor: C.line, minWidth: 130 }}>
                      <div className="truncate text-[11px] font-semibold" style={{ color: C.sub }} title={h}>{h || `(col ${i + 1})`}</div>
                      <select data-testid={`paste-map-${kind}-${i}`} value={map[i] || "Ignore"}
                        onChange={(e) => setMap((m) => m.map((x, j) => (j === i ? e.target.value : x)))}
                        className="mt-0.5 w-full bg-white text-[12px] font-semibold" style={{ color: C.ink }}>
                        <option>Ignore</option>
                        {fields.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 50).map((row, ri) => (
                  <tr key={ri}>
                    {parsed.headers.map((_, ci) => (
                      <td key={ci} className="border p-1 align-top" style={{ borderColor: C.line, color: C.ink }}>{row[ci] || ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-1.5 text-[12px]" style={{ color: C.sub }}>{parsed.rows.length} row(s){parsed.rows.length > 50 ? " (showing first 50)" : ""}</div>
          </div>
        )}
        <DialogFooter>
          <button data-testid="paste-cancel-btn" onClick={onClose} className="rounded-lg border px-4 py-2 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.ink }}>Cancel</button>
          <button data-testid="paste-save-btn" onClick={save} className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white" style={{ background: C.indigo }}>Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ClubsSection() {
  const [users, setUsers] = useState(null);
  const [setup, setSetup] = useState(null);
  const [officersRows, setOfficersRows] = useState(null);
  const [allocRows, setAllocRows] = useState(null);
  const [pasteFor, setPasteFor] = useState(null);
  const [schema, setSchema] = useState([]);
  const [res, setRes] = useState(null);
  const [roleAccess, setRoleAccess] = useState({});
  const [roleGran, setRoleGran] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [uploaded, setUploaded] = useState({ officers: false, allocation: false });

  useEffect(() => {
    api.get("/me/principal").then(async (r) => {
      if (!r.data.district_id) return;
      try {
        const u = await api.get(`/support/districts/${r.data.district_id}/uploads`);
        setUploaded({ officers: !!u.data.uploads?.officers, allocation: !!u.data.uploads?.allocation });
      } catch { /* none uploaded yet */ }
    }).catch(() => {});
    api.get("/district-setup/role-schema").then((r) => setSchema(r.data.roles || [])).catch(() => {});
  }, []);

  const tpl = (which) => { window.open(`${api.defaults.baseURL}/district-setup/templates/${which}`, "_blank"); };
  const allocFields = ["Club ID", "Club Name", "Zone",
    "District Governor Name", "District Governor Member ID", "District Governor Phone", "District Governor Email",
    ...schema.flatMap((r) => [`${r.label} Name`, `${r.label} Member ID`, `${r.label} Phone`, `${r.label} Email`]),
    "President Member ID", "President Phone", "President Email",
    "Secretary Member ID", "Secretary Phone", "Secretary Email"];
  // Each slot may come from a picked file, pasted rows, or an already-uploaded file.
  const haveOfficers = !!users || !!officersRows || uploaded.officers;
  const haveAllocation = !!setup || !!allocRows || uploaded.allocation;
  const buildFd = () => {
    const fd = new FormData();
    if (users) fd.append("users_file", users);
    else if (officersRows) fd.append("officers_rows", JSON.stringify(officersRows));
    if (setup) fd.append("setup_file", setup);
    else if (allocRows) fd.append("allocation_rows", JSON.stringify(allocRows));
    return fd;
  };
  const validate = async () => {
    if (!haveAllocation) { toast.error("Provide the club allocation document (upload, paste, or an already-uploaded file)"); return; }
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/district-setup/validate", buildFd());
      setRes(r.data);
      setRoleAccess(Object.fromEntries((r.data.configurable_roles || []).map((role) => [role, "limited"])));
      setRoleGran(Object.fromEntries((r.data.role_schema || []).map((s) => [s.label, s.granularity || "club"])));
    } catch (e) { toast.error(e.response?.data?.detail || "Validation failed"); }
    setBusy(false);
  };
  const downloadUnmatched = () => {
    const ms = res?.match_summary || {};
    const rows = [["Tab", "Role", "Person", "Club", "Email", "Status", "Reason"]];
    ["clubs", "district"].forEach((tab) => {
      Object.entries(ms[tab] || {}).forEach(([role, st]) => {
        (st.records || []).filter((x) => x.status !== "matched").forEach((x) => {
          rows.push([tab === "clubs" ? "Clubs" : "District", role, x.person || "", x.club || "", x.email || "", x.status, x.reason || ""]);
        });
      });
    });
    if (rows.length === 1) { toast.info("Nothing to export — everyone was matched"); return; }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "unmatched_report.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };
  const activate = async () => {
    setBusy(true);
    try {
      // Persist the discovered role structure (chosen granularity) before activation so the
      // allocation mapping uses the admin's per-zone / per-club choices.
      const schemaRoles = (res?.role_schema || []).map((s) => ({
        label: s.label, granularity: roleGran[s.label] === "zone" ? "zone" : "club" }));
      if (schemaRoles.length) {
        try { await api.put("/district-setup/role-schema", { roles: schemaRoles }); } catch (_) { /* non-blocking */ }
      }
      const fd = buildFd();
      fd.append("role_access", JSON.stringify(roleAccess));
      const r = await api.post("/district-setup/activate", fd);
      toast.success("District Setup Complete");
      setDone(r.data.mapping || true); setRes(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); }
    setBusy(false);
  };

  return (
    <Card className="border-2" style={{ borderColor: "#00A2BE" }}>
      <SectionTitle icon={Buildings} title="Clubs, zones & officers"
        sub="Upload one workbook with two tabs — 'Clubs' (one row per club: zone, President, Secretary, AG and any dynamic club roles) and 'District' (global roles like the Governor & Treasurer). Import the member repository first."
        right={<div className="flex items-center gap-2">
          <a href="/setup/allocation" data-testid="open-allocation-manager"
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: "#4B31C9" }}>
            <UsersThree size={15} weight="bold" /> Allocation Manager
          </a>
          {done && <StatusPill status="ok">Activated</StatusPill>}
        </div>} />

      <div className="mt-1 grid gap-3 lg:grid-cols-3">
        {/* Step 1 — Download the sample sheet (headers match this district's roles) */}
        <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line }}>
          <p className="text-[15px] font-semibold" style={{ color: C.ink }}>1 · Download the sample workbook</p>
          <p className="mb-2 text-[13px]" style={{ color: C.sub }}>Two tabs: <b>Clubs</b> (Club President, Club Secretary & Assistant Governor are fixed — add any other role columns you need) and <b>District</b> (Role Name · Member Name · Member Email).</p>
          <button data-testid="tpl-setup" onClick={() => tpl("setup")} className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13.5px] font-bold text-white" style={{ background: C.turquoise }}><DownloadSimple size={15} weight="bold" /> Download sample sheet</button>
        </div>

        {/* Step 2 — Add your two-tab workbook (upload or paste, following the sample headers) */}
        <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line }}>
          <p className="text-[15px] font-semibold" style={{ color: C.ink }}>2 · Add your workbook</p>
          <p className="mb-2 text-[13px]" style={{ color: C.sub }}>Upload the filled two-tab workbook (Clubs + District), or paste the Clubs rows straight from Excel.</p>
          <div className="flex flex-wrap items-center gap-2">
            <UploadButton testid="setup-setup-file" onPick={(f) => { setSetup(f); setAllocRows(null); setRes(null); }} />
            <button data-testid="paste-alloc-btn" onClick={() => setPasteFor("allocation")} className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}><ClipboardText size={14} weight="bold" /> Paste data</button>
          </div>
          <div className="mt-1.5 text-[12.5px]" style={{ color: C.sub }} data-testid="alloc-source">
            {setup ? <span style={{ color: C.green }}>Selected file: {setup.name}</span>
              : allocRows ? <span style={{ color: C.green }}>Pasted {allocRows.length} row(s)</span>
              : <UploadStatus kind="allocation" />}
          </div>
        </div>

        {/* Step 3 — Preview & activate */}
        <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line, background: "#FAFAFF" }}>
          <p className="text-[15px] font-semibold" style={{ color: C.ink }}>3 · Preview &amp; activate</p>
          <p className="mb-2 text-[13px]" style={{ color: C.sub }}>Check the mapping, then confirm to set up the district for this Rotary Year.</p>
          <div className="flex flex-col items-stretch gap-2">
            <PrimaryButton data-testid="setup-validate-btn" onClick={validate} disabled={busy || !haveAllocation}>Preview &amp; validate</PrimaryButton>
            {res?.can_activate && <GhostButton data-testid="setup-activate-btn" onClick={activate} disabled={busy}>Confirm setup</GhostButton>}
          </div>
          {!setup && !allocRows && haveAllocation && (
            <p className="mt-2 text-[12.5px]" style={{ color: C.sub }} data-testid="using-uploaded-note">Using your already-uploaded document — no need to pick it again.</p>
          )}
        </div>
      </div>

      {/* Optional legacy path: a separate District Officers Access sheet. Most districts don't
          need this — the one club document above already carries every official. */}
      <details className="mt-3 rounded-2xl border p-3.5" style={{ borderColor: C.line }} data-testid="officers-advanced">
        <summary className="cursor-pointer text-[13.5px] font-semibold" style={{ color: C.sub }}>Advanced: use a separate District Officers Access sheet (optional)</summary>
        <p className="mb-2 mt-2 text-[12.5px]" style={{ color: C.sub }}>Only if you keep district officers on their own sheet. Otherwise leave this blank — the club document already handles them.</p>
        <div className="flex flex-wrap items-center gap-2">
          <UploadButton testid="setup-users-file" onPick={(f) => { setUsers(f); setOfficersRows(null); setRes(null); }} />
          <button data-testid="paste-officers-btn" onClick={() => setPasteFor("officers")} className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}><ClipboardText size={14} weight="bold" /> Paste data</button>
          <button data-testid="tpl-officers" onClick={() => tpl("users")} className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}><DownloadSimple size={14} weight="bold" /> Sample</button>
        </div>
        <div className="mt-1.5 text-[12.5px]" style={{ color: C.sub }} data-testid="officers-source">
          {users ? <span style={{ color: C.green }}>Selected file: {users.name}</span>
            : officersRows ? <span style={{ color: C.green }}>Pasted {officersRows.length} row(s)</span>
            : <UploadStatus kind="officers" />}
        </div>
      </details>

      <PasteDialog open={pasteFor === "officers"} kind="officers" fields={OFFICER_FIELDS}
        onClose={() => setPasteFor(null)}
        onSave={(rows) => { setOfficersRows(rows); setUsers(null); setRes(null); setPasteFor(null); toast.success(`Saved ${rows.length} officer row(s)`); }} />
      <PasteDialog open={pasteFor === "allocation"} kind="allocation" fields={allocFields}
        onClose={() => setPasteFor(null)}
        onSave={(rows) => { setAllocRows(rows); setSetup(null); setRes(null); setPasteFor(null); toast.success(`Saved ${rows.length} club row(s)`); }} />

      <SummaryErrorBoundary resetKey={res || done || "idle"}>
      {busy && <div className="mt-3"><Loader label="Checking workbooks…" /></div>}
      {done && typeof done === "object" && (
        <div className="mt-4 rounded-2xl border p-3.5" style={{ borderColor: C.line, background: "#F1FAF4" }} data-testid="setup-mapping-report">
          <p className="text-[14px] font-bold" style={{ color: "#1E7A46" }}>Activated — here’s who was mapped</p>
          <ul className="mt-1.5 grid gap-0.5 text-[13.5px]" style={{ color: C.ink }}>
            {Object.entries(done.zone_roles || {}).map(([r, n]) => <li key={r}>{r}: <b>{peopleOf(n)}</b> {peopleOf(n) === 1 ? "person" : "people"} across {subOf(n, "zones")} zone(s)</li>)}
            {Object.entries(done.club_roles || {}).map(([r, n]) => <li key={r}>{r}: <b>{peopleOf(n)}</b> {peopleOf(n) === 1 ? "person" : "people"} across {subOf(n, "clubs")} club(s)</li>)}
            <li>Presidents: <b>{done.presidents || 0}</b> · Secretaries: <b>{done.secretaries || 0}</b></li>
          </ul>
          {(done.unmatched || []).length > 0 && (
            <div className="mt-2.5 rounded-xl border p-2.5" style={{ borderColor: "#F0C7C7", background: "#FDECEC" }} data-testid="setup-unmatched-panel">
              <p className="text-[13px] font-bold" style={{ color: C.cranberry }}>{done.unmatched.length} President/Secretary need a Member ID</p>
              <p className="text-[12px]" style={{ color: C.sub }}>They were assigned to their club, but couldn't be matched to a member. Add their Member ID / email later in the Allocation Manager or member repository.</p>
              <ul className="mt-1.5 max-h-52 space-y-0.5 overflow-auto text-[12.5px]" style={{ color: C.ink }}>
                {done.unmatched.slice(0, 200).map((u, i) => (
                  <li key={i}>{String(u.role || "")} — <b>{String(u.person || "")}</b>{u.club ? ` (${String(u.club)})` : ""}{u.reason ? `: ${String(u.reason)}` : ""}{u.email ? ` · ${String(u.email)}` : ""}</li>
                ))}
              </ul>
              <a href="/setup/allocation" data-testid="unmatched-open-manager" className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: "#4B31C9" }}>Fix in Allocation Manager →</a>
            </div>
          )}
        </div>
      )}
      {res && (
        <>
          <Summary data={res.summary} />
          {(res.errors || []).length > 0 && (
            <div className="mt-3 rounded-xl p-3 text-[14px]" style={{ background: "#FDECEC", color: C.cranberry }} data-testid="setup-errors">
              <b>Fix before confirming — nothing is saved yet:</b>
              <ul className="mt-1 list-disc pl-5">{res.errors.slice(0, 12).map((e, i) => <li key={i}>{typeof e === "string" ? e : (e.error || e.message || JSON.stringify(e))}</li>)}</ul>
            </div>
          )}
          {(res.warnings || []).length > 0 && (
            <div className="mt-3 rounded-xl p-3 text-[13.5px]" style={{ background: "#FFF7E8", color: "#8A5A00" }} data-testid="setup-warnings">
              <b>{res.warnings.length} note(s) — these rows were adjusted, nothing blocks activation:</b>
              <ul className="mt-1 max-h-52 list-disc space-y-0.5 overflow-auto pl-5">
                {res.warnings.slice(0, 60).map((w, i) => <li key={i}>{typeof w === "string" ? w : (w.warning || w.message || JSON.stringify(w))}</li>)}
              </ul>
            </div>
          )}
          {res.will_map && (
            <div className="mt-3 rounded-xl border p-3.5" style={{ borderColor: C.line }} data-testid="setup-will-map">
              <p className="text-[14px] font-bold" style={{ color: C.ink }}>Who will be mapped</p>
              <ul className="mt-1.5 grid gap-0.5 text-[13.5px]" style={{ color: C.ink }}>
                {Object.entries(res.will_map.zone_roles || {}).map(([r, n]) => (
                  <li key={r} data-testid={`willmap-zone-${r}`}>{r}: <b>{peopleOf(n)}</b> {peopleOf(n) === 1 ? "person" : "people"} across {subOf(n, "zones")} zone(s){peopleOf(n) === 0 && <span style={{ color: "#8A5A00" }}> — no column matched; check the role name matches your sheet</span>}</li>
                ))}
                {Object.entries(res.will_map.club_roles || {}).map(([r, n]) => (
                  <li key={r} data-testid={`willmap-club-${r}`}>{r}: <b>{peopleOf(n)}</b> {peopleOf(n) === 1 ? "person" : "people"} across {subOf(n, "clubs")} club(s){peopleOf(n) === 0 && <span style={{ color: "#8A5A00" }}> — no column matched; check the role name matches your sheet</span>}</li>
                ))}
                <li>Presidents: <b>{res.will_map.presidents || 0}</b> · Secretaries: <b>{res.will_map.secretaries || 0}</b></li>
              </ul>
            </div>
          )}
          {res.match_summary && (Object.keys(res.match_summary.clubs || {}).length > 0
            || Object.keys(res.match_summary.district || {}).length > 0) && (
            <div className="mt-3 rounded-xl border p-3.5" style={{ borderColor: C.line }} data-testid="setup-match-summary">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[14px] font-bold" style={{ color: C.ink }}>Mapping summary — review before you save</p>
                <button data-testid="download-unmatched-btn" onClick={downloadUnmatched}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold"
                  style={{ borderColor: C.line, color: C.azure }}>
                  <DownloadSimple size={14} weight="bold" /> Download unmatched report (CSV)
                </button>
              </div>
              <p className="mb-2 text-[12px]" style={{ color: C.sub }}>People who aren't found in the member repository are still onboarded by email; the invite will note we couldn't map their record. Each is flagged with a likely reason.</p>
              {["clubs", "district"].map((tab) => {
                const roles = res.match_summary[tab] || {};
                if (!Object.keys(roles).length) return null;
                return (
                  <div key={tab} className="mt-2" data-testid={`match-tab-${tab}`}>
                    <p className="text-[12.5px] font-bold uppercase tracking-wide" style={{ color: C.azure }}>{tab === "clubs" ? "Clubs tab" : "District tab"}</p>
                    <div className="mt-1 divide-y">
                      {Object.entries(roles).map(([role, st]) => (
                        <div key={role} className="py-1.5" data-testid={`match-role-${role}`}>
                          <div className="flex flex-wrap items-center justify-between gap-1 text-[13.5px]" style={{ color: C.ink }}>
                            <span className="font-semibold">{String(role)}</span>
                            <span className="text-[12.5px]">
                              <b>{Number(st?.matched || 0)}</b> matched
                              {Number(st?.onboarded_unmatched || 0) > 0 && <span style={{ color: "#8A5A00" }}> · {Number(st.onboarded_unmatched)} onboarded by email</span>}
                              {Number(st?.unresolved || 0) > 0 && <span style={{ color: C.cranberry }}> · {Number(st.unresolved)} unresolved</span>}
                            </span>
                          </div>
                          {(st?.records || []).filter((x) => x && x.status !== "matched").map((x, i) => (
                            <div key={i} className="pl-3 text-[12px]" style={{ color: C.sub }}>
                              <span style={{ color: x.status === "unresolved" ? C.cranberry : "#8A5A00", fontWeight: 700 }}>
                                {x.status === "unresolved" ? "Unresolved" : "Onboarded by email"}
                              </span>
                              {" — "}{String(x.person ?? "")}{x.club ? ` (${String(x.club)})` : ""}{x.reason ? `: ${String(x.reason)}` : ""}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(res.configurable_roles || []).length > 0 && (
            <div className="mt-3 rounded-xl border p-3.5" style={{ borderColor: C.line }} data-testid="setup-role-access">
              <p className="text-[14px] font-bold" style={{ color: C.ink }}>Configure detected roles</p>
              <p className="mb-2 text-[12.5px]" style={{ color: C.sub }}>These roles were detected from your sheets. <b>Per zone</b> = the person sees every club in their zone; <b>Per club</b> = only their allocated club(s). <b>Admin</b> = full district access; <b>View</b> (default) = only their allocated clubs plus district-wide membership &amp; foundation MIS. The District Governor always has full access; President &amp; Secretary are fixed club officers.</p>
              <div className="divide-y">
                {(res.configurable_roles || []).map((role) => {
                  const isSchema = (res.role_schema || []).some((s) => s.label === role);
                  return (
                  <div key={role} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`setup-role-${role}`}>
                    <span className="text-[13.5px] font-semibold" style={{ color: C.ink }}>{role}</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {isSchema && (
                        <div className="inline-flex overflow-hidden rounded-full text-[12px] font-bold" style={{ boxShadow: "inset 0 0 0 1.5px #DCE3EE" }} data-testid={`setup-gran-${role}`}>
                          {[["club", "Per club"], ["zone", "Per zone"]].map(([g, lbl]) => (
                            <button key={g} type="button" data-testid={`setup-gran-${role}-${g}`}
                              onClick={() => setRoleGran((m) => ({ ...m, [role]: g }))}
                              className="px-3 py-1 transition-colors"
                              style={(roleGran[role] || "club") === g ? { background: C.azure, color: "#fff" } : { color: C.azure }}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex rounded-full p-0.5" style={{ background: "#F1ECFF" }}>
                        {[["limited", "View"], ["admin", "Admin"]].map(([lv, lbl]) => (
                          <button key={lv} type="button" data-testid={`setup-role-${role}-${lv}`}
                            onClick={() => setRoleAccess((m) => ({ ...m, [role]: lv }))}
                            className="rounded-full px-3 py-1 text-[12px] font-bold transition-colors"
                            style={(roleAccess[role] || "limited") === lv ? { background: C.indigo, color: "#fff" } : { color: C.indigo }}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3.5" style={{ borderColor: C.line }} data-testid="setup-confirm-bar">
            {res.can_activate
              ? <p className="text-[13.5px] font-medium" style={{ color: C.green }}>Reviewed the mapping above? Save it to the district for this Rotary Year.</p>
              : <p className="text-[13.5px] font-medium" style={{ color: C.cranberry }}>{(() => { const e = res.errors && res.errors[0]; return (typeof e === "string" ? e : (e && (e.error || e.message))) || "Fix the issues above before you can save."; })()}</p>}
            <PrimaryButton data-testid="setup-confirm-activate-btn" onClick={activate} disabled={busy || !res.can_activate}>
              {busy ? "Saving…" : "Confirm & Activate"}
            </PrimaryButton>
          </div>
        </>
      )}
      </SummaryErrorBoundary>
    </Card>
  );
}

// 3) Annual GOV requirements ----------------------------------------------

// Inherited default list every district starts from (admin can add/remove/adjust).
const DEFAULT_REQUIREMENTS = [
  // Upload a document
  ["Charter Certificate", "document"], ["Club Byelaws", "document"],
  ["Club Plan and Objectives", "document"], ["Budget for the Year", "document"],
  ["SAR 1 Invoice", "document"], ["SAR 1 Payment Proof", "document"],
  ["SAR 2 Invoice", "document"], ["SAR 2 Payment Proof", "document"],
  ["Rotary News Trust Invoice", "document"], ["Rotary News Trust Payment Proof", "document"],
  ["District Dues Invoice", "document"], ["District Dues Payment Proof", "document"],
  ["Club Audited Accounts (Previous Year)", "document"], ["Trust Audited Accounts (Previous Year)", "document"],
  ["Planning Guide", "document"], ["TRF Contribution Records", "document"],
  // Fill in the app
  ["Unfilled Classifications", "info"],
  ["Club Meeting Attendance", "info"], ["Board Meeting Attendance", "info"],
  // Either — fill in or upload
  ["Club Board", "both"], ["Club Committees", "both"], ["Club Sub-committees", "both"],
  ["Rotary Clubs Sponsored — List with President Contact and GSR/NCA", "both"],
  ["Rotaract Clubs Sponsored — List with President Contact", "both"],
  ["Interact Clubs Sponsored — List with President Contact", "both"],
  ["Rotary Community Corps Sponsored — List with President Contact", "both"],
].map(([name, kind]) => ({ name, kind, required: false, instruction: "" }));

function RequirementsSection() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    api.get("/admin/checklist")
      .then((r) => setItems((r.data.items || []).length ? r.data.items : DEFAULT_REQUIREMENTS.map((x) => ({ ...x }))))
      .catch(() => setItems(DEFAULT_REQUIREMENTS.map((x) => ({ ...x }))));
  }, []);
  if (!items) return <Card className="border-2" style={{ borderColor: "#22B07D" }}><SectionTitle icon={ClipboardText} title="Configure Document Requirement for GOV" /><Loader /></Card>;

  const upd = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add = (kind = "document") => setItems((arr) => [...arr, { name: "", kind, required: true, disabled: false, instruction: "" }]);
  const setDisabled = (i, val) => upd(i, "disabled", val);
  const resetDefaults = () => setItems(DEFAULT_REQUIREMENTS.map((x) => ({ ...x })));
  const requiredCount = items.filter((it) => (it.name || "").trim() && !it.disabled).length;
  const save = async () => {
    const clean = items.filter((it) => (it.name || "").trim());
    setBusy(true);
    try { await api.post("/admin/checklist", { items: clean }); toast.success("Requirements saved"); setItems(clean); setEditing(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (!editing) {
    return (
      <Card className="border-2" style={{ borderColor: "#22B07D" }}>
        <SectionTitle icon={ClipboardText} title="Configure Document Requirement for GOV"
          right={<button data-testid="req-edit" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: C.indigo }}><PencilSimple size={15} weight="bold" /> Edit</button>} />
        <div className="mt-1 flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: "#F1FAF4" }} data-testid="req-summary">
          <CheckCircle size={20} weight="fill" style={{ color: "#22B07D" }} />
          <p className="text-[15px] font-bold" style={{ color: C.ink }}>{requiredCount} document{requiredCount === 1 ? "" : "s"} required</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-2" style={{ borderColor: "#22B07D" }}>
      <SectionTitle icon={ClipboardText} title="Configure Document Requirement for GOV" />
      <div className="space-y-4" data-testid="requirements-list">
        {DOC_CATS.map((cat) => {
          const rows = items.map((it, i) => [it, i]).filter(([it]) => cat.kinds.includes(it.kind || "document"));
          return (
            <div key={cat.key} className="rounded-2xl border p-5 shadow-sm" style={{ borderColor: C.line, background: "#fff" }} data-testid={`req-cat-${cat.key}`}>
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl" style={{ background: C.indigoSoft, color: C.indigo }}>
                    <cat.Icon size={18} weight="bold" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold" style={{ color: C.ink }}>{cat.title}</p>
                    <p className="text-[12.5px]" style={{ color: C.sub }}>{cat.hint}</p>
                  </div>
                </div>
                <button data-testid={`req-add-${cat.key}`} onClick={() => add(cat.add)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-bold text-white transition-colors" style={{ background: C.indigo }}>
                  <Plus size={16} weight="bold" /> Add more documents
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map(([it, i]) => {
                  const DocIcon = iconFor(it.name);
                  const off = !!it.disabled;
                  return (
                    <div key={i} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5" data-testid={`req-row-${i}`}
                      style={{ background: off ? "#FBE9EE" : "#F7F7FB", opacity: off ? 0.85 : 1 }}>
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white" style={{ color: off ? C.cranberry : C.indigo }}>
                        <DocIcon size={16} weight="regular" />
                      </span>
                      <input data-testid={`req-name-${i}`} value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Document name" disabled={off}
                        className="min-w-0 flex-1 rounded-md bg-transparent text-[14px] font-semibold outline-none" style={{ color: off ? C.cranberry : C.ink, textDecoration: off ? "line-through" : "none" }} />
                      {off ? (
                        <button data-testid={`req-addback-${i}`} onClick={() => setDisabled(i, false)} className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold" style={{ color: "#1E7A46", boxShadow: "inset 0 0 0 1.5px #BFE6CC" }}>
                          <Plus size={12} weight="bold" /> Add back
                        </button>
                      ) : (
                        <button data-testid={`req-del-${i}`} onClick={() => setDisabled(i, true)} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg" style={{ color: C.cranberry }} title="Mark not required"><Trash size={14} weight="bold" /></button>
                      )}
                    </div>
                  );
                })}
                {rows.length === 0 && <p className="py-1 text-[13px] sm:col-span-2 lg:col-span-3" style={{ color: C.sub }}>None yet — use “Add more documents”.</p>}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-[15px]" style={{ color: C.sub }}>No documents yet — load the standard list to begin.</p>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <p className="mr-auto text-[12.5px]" style={{ color: C.sub }}>Marked <span style={{ color: C.cranberry }}>not required</span> stay here for reference and can be added back. Clubs only see the {requiredCount} required.</p>
        <GhostButton data-testid="req-reset" onClick={resetDefaults}><ClipboardText size={15} weight="bold" /> Load standard list</GhostButton>
        <PrimaryButton data-testid="req-save" onClick={save} disabled={busy}><CheckCircle size={16} weight="bold" /> Save requirements</PrimaryButton>
      </div>
    </Card>
  );
}

// Private auto-updating calendar subscription (Option B).
function SubscribeCalendar({ testid }) {
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);
  const get = async () => {
    if (url) { setOpen((o) => !o); return; }
    try {
      const r = await api.get("/me/calendar-feed");
      setUrl(`${api.defaults.baseURL}${r.data.path}`); setOpen(true);
    } catch { toast.error("Could not create calendar link"); }
  };
  const webcal = url.replace(/^https?:\/\//, "webcal://");
  return (
    <div className="w-full">
      <button data-testid={testid} onClick={get}
        className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>
        <CalendarPlus size={14} weight="bold" /> Subscribe (auto-updates)
      </button>
      {open && url && (
        <div className="mt-2 rounded-xl border p-3" style={{ borderColor: C.line, background: "#F7F7FB" }} data-testid={`${testid}-panel`}>
          <p className="mb-2 text-[12.5px]" style={{ color: C.sub }}>Add this private link once in your phone's calendar as a “subscription”. New and changed GOV dates then appear automatically.</p>
          <div className="flex flex-wrap items-center gap-2">
            <input readOnly value={url} data-testid={`${testid}-url`} onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded-md border bg-white px-2.5 py-1.5 text-[12.5px]" style={{ borderColor: C.line, color: C.ink }} />
            <button data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }}
              className="rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-white" style={{ background: C.indigo }}>Copy</button>
            <a data-testid={`${testid}-open`} href={webcal} className="rounded-md border px-2.5 py-1.5 text-[12.5px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>Open in calendar</a>
          </div>
        </div>
      )}
    </div>
  );
}

// Change-President popup: type-ahead search restricted to THIS club's members.
function ChangePresidentDialog({ open, onClose, districtId, club, onSaved }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && club) {
      setData(null); setQ(""); setSel(null);
      api.get(`/support/districts/${districtId}/clubs/${club.club_id}/members`)
        .then((r) => setData(r.data)).catch(() => { toast.error("Could not load members"); setData({ members: [] }); });
    }
  }, [open, club, districtId]);
  if (!club) return null;
  const members = data?.members || [];
  const curId = data?.current_president_member_id;
  const curName = curId ? (members.find((m) => m.member_id === curId)?.full_name || curId) : "—";
  const matches = q.trim()
    ? members.filter((m) => (m.full_name || "").toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];
  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.post(`/support/districts/${districtId}/clubs/${club.club_id}/president`, { member_id: sel.member_id });
      toast.success(`President set to ${sel.full_name || sel.member_id}`); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="president-dialog">
        <DialogHeader>
          <DialogTitle>Change President — {club.club_name}</DialogTitle>
          <DialogDescription>Search this club's members and pick the new President.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader label="Loading members…" /> : (
          <div className="space-y-3">
            <p className="text-[13px]" style={{ color: C.sub }}>Current President: <b style={{ color: C.ink }}>{curName}</b></p>
            <div>
              <input data-testid="president-search" autoFocus value={sel ? (sel.full_name || sel.member_id) : q}
                onChange={(e) => { setSel(null); setQ(e.target.value); }}
                placeholder="Type a member's name…"
                className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              {!sel && q.trim() && (
                <div className="mt-1 max-h-56 overflow-auto rounded-lg border" style={{ borderColor: C.line }} data-testid="president-matches">
                  {matches.length ? matches.map((m) => (
                    <button key={m.member_id} data-testid={`president-option-${m.member_id}`} onClick={() => { setSel(m); setQ(""); }}
                      className="block w-full px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#F5F3FF]" style={{ color: C.ink }}>
                      {m.full_name || m.member_id}
                    </button>
                  )) : <p className="px-3 py-2 text-[13px]" style={{ color: C.sub }}>No members match “{q.trim()}”.</p>}
                </div>
              )}
            </div>
            {sel && <p className="rounded-lg px-3 py-2 text-[13px]" style={{ background: "#F1FAF4", color: "#1E7A46" }}>New President: <b>{sel.full_name || sel.member_id}</b></p>}
          </div>
        )}
        <DialogFooter>
          <GhostButton data-testid="president-cancel" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="president-save" onClick={save} disabled={busy || !sel}>{busy ? "Saving…" : "Confirm & save"}</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// 4) Clubs + GOV visit schedule — ONE unified list. Each card carries the GOV date/initiate
// tool; the club-management actions (change President, mark inactive/activate, terminate) are
// tucked behind a 3-dot menu and each asks for confirmation. Grouped by zone, clubs A-Z.
function GovScheduleSection() {
  const [sched, setSched] = useState(null);      // /admin/gov-schedule (config gate + RY dates)
  const [govMap, setGovMap] = useState({});      // club_id -> { initiated, scheduled_date }
  const [clubs, setClubs] = useState(null);      // /support .../clubs (status, members, zone)
  const [zones, setZones] = useState([]);        // zone-options (for Add club form)
  const [districtId, setDistrictId] = useState(null);
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(null);        // holds club_id being acted on, or "all"
  const [upResult, setUpResult] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [menuFor, setMenuFor] = useState(null);  // club_id whose 3-dot menu is open
  const [presFor, setPresFor] = useState(null);  // club object for the president dialog
  const [zoneTab, setZoneTab] = useState(null);  // active zone tab
  const [govDialog, setGovDialog] = useState(null); // {clubId, clubName} for the Initiate-GOV dialog

  const load = async () => {
    const p = await api.get("/me/principal").then((r) => r.data).catch(() => ({}));
    setDistrictId(p.district_id || null);
    const [s, c, z] = await Promise.all([
      api.get("/admin/gov-schedule").then((r) => r.data).catch(() => ({ clubs: [] })),
      p.district_id ? api.get(`/support/districts/${p.district_id}/clubs`).then((r) => r.data.clubs || []).catch(() => []) : Promise.resolve([]),
      p.district_id ? api.get(`/support/districts/${p.district_id}/zone-options`).then((r) => r.data.zones || []).catch(() => []) : Promise.resolve([]),
    ]);
    setSched(s);
    setGovMap(Object.fromEntries((s.clubs || []).map((g) => [g.club_id, g])));
    setClubs(c);
    setZones(z);
    setDates(Object.fromEntries((s.clubs || []).map((g) => [g.club_id, g.scheduled_date || g.planned_gov_date || ""])));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!clubs || !sched) return <Card><SectionTitle icon={CalendarBlank} title="Clubs & GOV visit schedule" /><Loader /></Card>;

  const gov = (cid) => govMap[cid] || {};
  const saveAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/admin/gov-schedule", { dates });
      if (r.data.skipped_past) toast.warning(`Schedule saved · ${r.data.skipped_past} past date(s) skipped (GOV dates can't be retrospective)`);
      else toast.success("Schedule saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(null);
  };
  const initiateClub = async (cid) => {
    if (!dates[cid]) return;
    setBusy(cid);
    try {
      await api.post("/admin/gov-schedule", { dates: { [cid]: dates[cid] } });
      const r = await api.post("/admin/gov-schedule/initiate", { club_ids: [cid] });
      if (r.data.created) toast.success("GOV visit created");
      else toast.message("Nothing to initiate for this club");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not initiate"); }
    setBusy(null);
  };
  const uploadSchedule = async (file) => {
    if (!file) return;
    setBusy("all"); setUpResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/gov-schedule/upload", fd);
      setUpResult(r.data);
      toast.success(`Schedule updated for ${r.data.updated} club(s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the file"); }
    setBusy(null);
  };
  const saveReschedule = async (cid) => {
    if (!dates[cid]) return;
    setBusy(cid);
    try {
      const r = await api.post("/admin/gov-schedule/reschedule", { club_id: cid, date: dates[cid] });
      const wa = r.data.whatsapp_sent ? ` · WhatsApp ${r.data.whatsapp_sent}` : "";
      toast.success(`Visit rescheduled · everyone re-notified${wa}`); setEditingId(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reschedule"); }
    setBusy(null);
  };
  const initiateAll = async () => {
    setBusy("all");
    try {
      await api.post("/admin/gov-schedule", { dates });
      const r = await api.post("/admin/gov-schedule/initiate", {});
      const wa = r.data.whatsapp_sent ? ` · WhatsApp ${r.data.whatsapp_sent}` : "";
      toast.success(`Initiated ${r.data.created} visit(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}${wa}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not initiate"); }
    setBusy(null);
  };
  const setStatus = async (c, status, prompt) => {
    setMenuFor(null);
    if (!window.confirm(prompt)) return;
    try { await api.post(`/support/districts/${districtId}/clubs/${c.club_id}/status`, { status }); toast.success("Updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const icsUrl = (cid) => `${api.defaults.baseURL}/clubs/${cid}/gov/calendar.ics`;

  // Group by zone (A-Z), clubs A-Z within each zone; unzoned clubs last.
  const grouped = {};
  clubs.forEach((c) => { const z = c.zone || "No zone"; (grouped[z] = grouped[z] || []).push(c); });
  Object.values(grouped).forEach((arr) => arr.sort((a, b) => (a.club_name || "").localeCompare(b.club_name || "")));
  const zoneNames = Object.keys(grouped).sort((a, b) => (a === "No zone" ? 1 : b === "No zone" ? -1 : a.localeCompare(b)));
  const pubOff = !sched.has_published_config;
  const pendingCount = clubs.filter((c) => !gov(c.club_id).initiated && dates[c.club_id]).length;
  const activeZone = (zoneTab && grouped[zoneTab]) ? zoneTab : zoneNames[0];
  const shownClubs = activeZone ? grouped[activeZone] : [];
  // GOV dates cannot be retrospective: earliest selectable date is the later of today and RY start.
  const minDate = [sched.ry_start && sched.ry_start.slice(0, 10), sched.today].filter(Boolean).sort().pop();

  return (
    <Card className="border-2" style={{ borderColor: "#7C4DFF" }}>
      <SectionTitle icon={CalendarBlank} title="Clubs & GOV visit schedule"
        sub="Every club, grouped by zone. Set a Governor's Official Visit date and initiate the visit on the card. Manage a club (President, inactive, terminate) from its ⋮ menu." />

      {/* Bulk tools */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button data-testid="gov-schedule-template" onClick={() => window.open(`${api.defaults.baseURL}/admin/gov-schedule/template`, "_blank")}
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>
          <DownloadSimple size={14} weight="bold" /> Schedule template
        </button>
        <label data-testid="gov-schedule-upload" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>
          <UploadSimple size={14} weight="bold" /> Upload schedule (.xlsx)
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { uploadSchedule(e.target.files[0]); e.target.value = ""; }} />
        </label>
        <GhostButton data-testid="gov-schedule-save" onClick={saveAll} disabled={busy !== null}><CheckCircle size={15} weight="bold" /> Save all dates</GhostButton>
        <PrimaryButton data-testid="gov-initiate-all" onClick={initiateAll} disabled={busy !== null || pubOff || pendingCount === 0}
          title={pubOff ? "Confirm the clubs & documents setup first" : (pendingCount === 0 ? "No dated clubs left to initiate" : "")}>
          <RocketLaunch size={15} weight="bold" /> Initiate all scheduled ({pendingCount})
        </PrimaryButton>
        <SubscribeCalendar testid="gov-subscribe" />
      </div>
      {govDialog && (
        <InitiateGovDialog clubId={govDialog.clubId} clubName={govDialog.clubName} minDate={minDate} mode={govDialog.mode}
          onClose={() => setGovDialog(null)} onDone={load} />
      )}
      {upResult && (upResult.unknown?.length > 0 || upResult.bad_date?.length > 0 || upResult.past_date?.length > 0) && (
        <div className="mb-3 rounded-lg px-3 py-2 text-[13px]" style={{ background: "#FFF6E5", color: "#8A6100" }} data-testid="gov-upload-notes">
          {upResult.unknown?.length > 0 && <p>Not matched (skipped): {upResult.unknown.join(", ")}</p>}
          {upResult.bad_date?.length > 0 && <p>Date outside the Rotary Year (skipped): {upResult.bad_date.join(", ")}</p>}
          {upResult.past_date?.length > 0 && <p>Date in the past — GOV dates can't be retrospective (skipped): {upResult.past_date.join(", ")}</p>}
        </div>
      )}
      {pubOff && (
        <div className="mb-2 rounded-lg px-3 py-2 text-[13px]" style={{ background: "#FFF6E5", color: "#8A6100" }} data-testid="gov-config-warning">
          You can set dates now. To initiate visits, first confirm the clubs & documents setup above.
        </div>
      )}

      <div className="space-y-3" data-testid="gov-schedule">
        {zoneNames.length === 0 && <p className="text-[15px]" style={{ color: C.sub }}>No clubs yet — complete the club allocation first.</p>}
        {zoneNames.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="zone-tabs">
            {zoneNames.map((zone) => {
              const on = zone === activeZone;
              return (
                <button key={zone} data-testid={`zone-tab-${zone}`} onClick={() => setZoneTab(zone)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors"
                  style={on ? { background: C.indigo, color: "#fff" } : { color: C.indigo, boxShadow: "inset 0 0 0 1.5px #DED6FF" }}>
                  {zone}
                  <span className="rounded-full px-1.5 text-[11px] font-bold" style={on ? { background: "rgba(255,255,255,0.25)", color: "#fff" } : { background: "#EEE9FF", color: C.indigo }}>{grouped[zone].length}</span>
                </button>
              );
            })}
          </div>
        )}
        {activeZone && (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" data-testid={`zone-clubs-${activeZone}`}>
            {shownClubs.map((c) => {
              const cid = c.club_id;
              const g = gov(cid);
              const isBusy = busy === cid;
              const completed = g.initiated && g.visit_status === "completed";
              const inProgress = g.initiated && !completed;
              const cardStyle = completed
                ? { borderColor: "#8FD3A6", background: "#F1FAF4" }
                : inProgress
                ? { borderColor: "#F0C86A", background: "#FFFAEE" }
                : { borderColor: C.line, background: "#fff" };
              return (
                  <div key={cid} className="relative rounded-xl border p-3" style={{ ...cardStyle, opacity: c.status === "inactive" ? 0.72 : 1 }} data-testid={`gov-club-${cid}`}>
                    <div className="flex items-start gap-2">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: C.indigoSoft, color: C.indigo }}>
                        <CalendarCheck size={16} weight="bold" />
                      </span>
                      <span className="min-w-0 flex-1 break-words text-[14px] font-semibold leading-tight" style={{ color: C.ink }}>{c.club_name}</span>
                      {c.status === "inactive" && <StatusBadge status="inactive" />}
                      <button data-testid={`club-menu-${cid}`} onClick={() => setMenuFor(menuFor === cid ? null : cid)}
                        className="grid h-7 w-7 shrink-0 place-items-center rounded-lg transition-colors hover:bg-[#F1F1F5]" style={{ color: C.sub }} aria-label="Club actions">
                        <DotsThreeVertical size={18} weight="bold" />
                      </button>
                    </div>
                    <div className="mt-1 flex items-center gap-2 pl-10">
                      <span className="text-[12px]" style={{ color: C.sub }}>{c.members} member{c.members === 1 ? "" : "s"}</span>
                      {completed && (
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#DFF3E6", color: "#1E7A46" }} data-testid={`gov-completed-${cid}`}>Completed</span>
                      )}
                      {inProgress && (
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#FBEFCF", color: "#8A6D00" }} data-testid={`gov-initiated-${cid}`}>Visit underway</span>
                      )}
                    </div>

                    {g.initiated ? (
                      editingId === cid ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2 pl-10">
                          <DateField value={dates[cid] || ""} testid={`gov-reschedule-date-${cid}`}
                            min={minDate} max={sched.ry_end}
                            onChange={(v) => setDates({ ...dates, [cid]: v })} />
                          <button data-testid={`gov-reschedule-save-${cid}`} onClick={() => saveReschedule(cid)} disabled={isBusy || !dates[cid]}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-bold text-white" style={{ background: C.indigo }}>
                            {isBusy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Save
                          </button>
                          <button data-testid={`gov-reschedule-cancel-${cid}`} onClick={() => setEditingId(null)} className="text-[13px] font-semibold" style={{ color: C.sub }}>Cancel</button>
                        </div>
                      ) : (
                        <div className="mt-2 flex flex-wrap items-center gap-3 pl-10">
                          <span className="text-[13px] font-medium" style={{ color: C.ink }}>{formatDate(g.scheduled_date)}</span>
                          <button data-testid={`gov-reschedule-${cid}`} onClick={() => { setEditingId(cid); setDates({ ...dates, [cid]: g.scheduled_date || "" }); }}
                            className="inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: C.azure }}>
                            <CalendarCheck size={14} weight="bold" /> Edit date
                          </button>
                          <a data-testid={`gov-ics-${cid}`} href={icsUrl(cid)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: C.azure }}>
                            <CalendarPlus size={14} weight="bold" /> Add to calendar
                          </a>
                          <button data-testid={`gov-resend-${cid}`} onClick={() => setGovDialog({ clubId: cid, clubName: c.club_name, mode: "resend" })}
                            className="inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: C.indigo }}>
                            <PaperPlaneTilt size={14} weight="bold" /> Resend invite
                          </button>
                        </div>
                      )
                    ) : (
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-10">
                        <DateField value={dates[cid] || ""} testid={`gov-date-${cid}`}
                          min={minDate} max={sched.ry_end}
                          onChange={(v) => setDates({ ...dates, [cid]: v })} />
                        <button data-testid={`gov-initiate-${cid}`} onClick={() => setGovDialog({ clubId: cid, clubName: c.club_name })}
                          disabled={pubOff || isBusy}
                          title={pubOff ? "Confirm the clubs & documents setup first" : "Initiate the visit and notify both sides"}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold transition-colors"
                          style={{ background: pubOff ? "#EEEFF4" : C.indigo, color: pubOff ? C.sub : "#fff" }}>
                          {isBusy ? <CircleNotch size={14} className="animate-spin" /> : <RocketLaunch size={14} weight="bold" />} Initiate GOV
                        </button>
                      </div>
                    )}

                    {/* 3-dot overflow menu (hidden until opened) */}
                    {menuFor === cid && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuFor(null)} data-testid={`club-menu-backdrop-${cid}`} />
                        <div className="absolute right-2 top-11 z-20 w-52 overflow-hidden rounded-xl border bg-white py-1 shadow-lg" style={{ borderColor: C.line }} data-testid={`club-menu-panel-${cid}`}>
                          <button data-testid={`club-president-change-${cid}`} onClick={() => { setMenuFor(null); setPresFor(c); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#F5F3FF]" style={{ color: C.ink }}>
                            <IdentificationCard size={15} weight="bold" style={{ color: C.azure }} /> Set / change President
                          </button>
                          {c.status === "inactive" ? (
                            <button data-testid={`club-activate-${cid}`} onClick={() => setStatus(c, "active", `Reactivate "${c.club_name}"?`)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#F1FAF4]" style={{ color: "#1E7A46" }}>
                              <CheckCircle size={15} weight="bold" /> Activate club
                            </button>
                          ) : (
                            <button data-testid={`club-inactivate-${cid}`} onClick={() => setStatus(c, "inactive", `Mark "${c.club_name}" inactive? It stays in records but is flagged inactive.`)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#FFF7E8]" style={{ color: "#9A6700" }}>
                              <PencilSimple size={15} weight="bold" /> Mark inactive
                            </button>
                          )}
                          <button data-testid={`club-terminate-${cid}`} onClick={() => setStatus(c, "terminated", `Terminate "${c.club_name}"? It will be hidden from the list but not deleted.`)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#FDECEC]" style={{ color: C.cranberry }}>
                            <Trash size={15} weight="bold" /> Terminate club
                          </button>
                        </div>
                      </>
                    )}
                  </div>
              );
            })}
          </div>
        )}
      </div>

      <ChangePresidentDialog open={!!presFor} club={presFor} districtId={districtId}
        onClose={() => setPresFor(null)} onSaved={load} />
    </Card>
  );
}

// 5) Directory cover (RI President, District Governor, annual RI logo) ------
function CoverPhoto({ testid, label, kind, has, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", f);
      await api.post("/admin/cover-asset", fd);
      toast.success(`${label} uploaded`); setVer((v) => v + 1); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const src = `${api.defaults.baseURL}/admin/cover-image/${kind}?v=${ver}`;
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full border" style={{ borderColor: C.line, background: "#F4F5FA" }}>
        {(has || ver > 0)
          ? <img data-testid={`${testid}-img`} src={src} alt={label} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <Image size={22} style={{ color: C.sub }} />}
      </div>
      <label data-testid={testid} className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>
        {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />} {(has || ver > 0) ? "Replace photo" : "Upload photo"}
        <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} />
      </label>
    </div>
  );
}

function DirectoryCoverSection() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/annual-settings").then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);
  if (s === null) return <Card><SectionTitle icon={IdentificationCard} title="Upload assets" /><Loader /></Card>;
  const saveNames = async () => {
    setBusy(true);
    try { await api.post("/admin/ri-president", { ri_president: s.ri_president || "", district_governor: s.district_governor || "" }); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Card className="border-2" style={{ borderColor: "#00A2BE" }}>
      <SectionTitle icon={IdentificationCard} title="Upload assets" />
      <div className="mt-1 space-y-2.5">
        <div className="rounded-xl p-3" style={{ background: "#F7F7FB" }}>
          <p className="mb-1.5 text-[12.5px] font-bold" style={{ color: C.ink }}>RI President</p>
          <input data-testid="ri-president-input" value={s.ri_president || ""} onChange={(e) => setS({ ...s, ri_president: e.target.value })}
            placeholder="RI President name (optional)"
            className="mb-2 min-h-[40px] w-full rounded-lg border bg-white px-3 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
          <CoverPhoto testid="ri-president-photo" label="RI President photo" kind="ri_president_photo" has={s.has_ri_president_photo} onChanged={load} />
        </div>
        <div className="rounded-xl p-3" style={{ background: "#F7F7FB" }}>
          <p className="mb-1.5 text-[12.5px] font-bold" style={{ color: C.ink }}>District Governor</p>
          <input data-testid="dg-name-input" value={s.district_governor || ""} onChange={(e) => setS({ ...s, district_governor: e.target.value })}
            placeholder="District Governor name (optional)"
            className="mb-2 min-h-[40px] w-full rounded-lg border bg-white px-3 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
          <CoverPhoto testid="dg-photo" label="District Governor photo" kind="dg_photo" has={s.has_dg_photo} onChanged={load} />
        </div>
        <div className="rounded-xl p-3" style={{ background: "#F7F7FB" }}>
          <p className="mb-1.5 text-[12.5px] font-bold" style={{ color: C.ink }}>District logo</p>
          <CoverPhoto testid="ri-logo" label="District lockup logo" kind="ri_logo" has={s.has_ri_logo} onChanged={load} />
        </div>
      </div>
      <div className="mt-3">
        <PrimaryButton data-testid="cover-names-save" onClick={saveNames} disabled={busy}>
          <CheckCircle size={16} weight="bold" /> Save names
        </PrimaryButton>
      </div>
    </Card>
  );
}


function RoleAccessSection() {
  const [did, setDid] = useState(null);
  const [roles, setRoles] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (d) => setRoles((await api.get(`/support/districts/${d}/role-access`)).data.roles || []);
  useEffect(() => {
    api.get("/me/principal").then((r) => { setDid(r.data.district_id); if (r.data.district_id) load(r.data.district_id); }).catch(() => setRoles([]));
  }, []); // eslint-disable-line
  const setLevel = (role, level) => setRoles((rs) => rs.map((x) => x.role === role ? { ...x, level } : x));
  const toggleMock = (role) => setRoles((rs) => rs.map((x) => x.role === role ? { ...x, mock_gov: !x.mock_gov } : x));
  const save = async () => {
    setBusy(true);
    try {
      const levels = Object.fromEntries(roles.map((r) => [r.role, r.level]));
      const mock_gov = Object.fromEntries(roles.map((r) => [r.role, !!r.mock_gov]));
      await api.put(`/support/districts/${did}/role-access`, { levels, mock_gov });
      toast.success("Role access saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  return (
    <Card className="border-2" style={{ borderColor: "#F0577D" }}>
      <SectionTitle icon={UsersThree} title="Role access & functions"
        sub="Give each district role full access (Admin) or limited to their own area, and optionally let a role take part in the Mock GOV handshake. The District Governor always has full access." />
      {!roles ? <Loader label="Loading roles…" /> : roles.length === 0 ? (
        <p className="mt-2 text-[13.5px]" style={{ color: C.sub }}>No configurable roles yet — they appear here once officials are imported.</p>
      ) : (
        <>
          <div className="mt-3 divide-y" data-testid="role-access-list">
            {roles.map((r) => (
              <div key={r.role} className="flex flex-wrap items-center justify-between gap-3 py-2.5" data-testid={`role-access-${r.role}`}>
                <span className="text-[14px] font-semibold" style={{ color: C.ink }}>{r.role}</span>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid={`role-${r.role}-mock`} onClick={() => toggleMock(r.role)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold transition-colors"
                    style={r.mock_gov ? { background: "#E7F6EC", color: "#1E7A46" } : { background: "#F1F1F5", color: C.sub }}
                    title="Take part in the Mock GOV handshake">
                    <CheckCircle size={13} weight={r.mock_gov ? "fill" : "regular"} /> Mock GOV
                  </button>
                  <div className="flex rounded-full p-0.5" style={{ background: "#F1ECFF" }}>
                    {["limited", "admin"].map((lv) => (
                      <button key={lv} data-testid={`role-${r.role}-${lv}`} onClick={() => setLevel(r.role, lv)}
                        className="rounded-full px-3 py-1 text-[12.5px] font-bold capitalize transition-colors"
                        style={r.level === lv ? { background: C.indigo, color: "#fff" } : { color: C.indigo }}>
                        {lv === "admin" ? "Admin (full)" : "Limited"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <PrimaryButton data-testid="role-access-save" className="mt-4" onClick={save} disabled={busy}>Save role access</PrimaryButton>
        </>
      )}
    </Card>
  );
}


function StatusBadge({ status }) {
  const map = { active: ["#E7F6EC", "#1E7A46", "Active"], inactive: ["#FFF4E5", "#9A6700", "Inactive"] };
  const [bg, fg, label] = map[status] || map.active;
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: bg, color: fg }}>{label}</span>;
}

function AddClubCard() {
  const [districtId, setDistrictId] = useState(null);
  const [zones, setZones] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    const p = await api.get("/me/principal").then((r) => r.data).catch(() => ({}));
    setDistrictId(p.district_id || null);
    if (p.district_id) {
      setZones(await api.get(`/support/districts/${p.district_id}/zone-options`).then((r) => r.data.zones || []).catch(() => []));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const ags = (zones || []).flatMap((z) => (z.assistant_governors || [])
    .filter((a) => a.email)
    .map((a) => ({ email: a.email, name: a.name || a.email, zone_id: z.zone_id, zone_name: z.name })));
  return (
    <Card data-testid="add-club-card" className="border-2" style={{ borderColor: "#F7A81B" }}>
      <SectionTitle icon={Plus} title="Add new club" />
      <p className="mb-3 text-[13px]" style={{ color: C.sub }}>Add a club mid-year. Pick its Assistant Governor and the club is placed in that AG's zone automatically — Zone Governor &amp; Secretary carry over from the zone.</p>
      <PrimaryButton data-testid="open-add-club" className="w-full justify-center" onClick={() => setOpen(true)}>
        <Plus size={16} weight="bold" /> Add new club
      </PrimaryButton>
      {ags.length === 0 && districtId && (
        <p className="mt-2 text-[12.5px]" style={{ color: "#9A6700" }}>No Assistant Governors yet — they come from the club allocation. Add them before adding a club.</p>
      )}
      <AddClubDialog open={open} onClose={() => setOpen(false)} districtId={districtId} ags={ags} zones={zones} onAdded={load} />
    </Card>
  );
}

const EMPTY_OFFICER = { name: "", member_id: "", phone: "", email: "" };

function AddClubDialog({ open, onClose, districtId, ags, zones, onAdded }) {
  const [f, setF] = useState({ club_name: "", rotary_club_id: "", ag_email: "" });
  const [pres, setPres] = useState({ ...EMPTY_OFFICER });
  const [sec, setSec] = useState({ ...EMPTY_OFFICER });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF({ club_name: "", rotary_club_id: "", ag_email: "" }); setPres({ ...EMPTY_OFFICER }); setSec({ ...EMPTY_OFFICER }); setFile(null); }
  }, [open]);
  const ag = ags.find((a) => a.email === f.ag_email);
  const agZone = ag && (zones || []).find((z) => z.zone_id === ag.zone_id);
  const noAg = ags.length === 0;
  const inp = "min-h-[40px] w-full rounded-lg border px-3 text-[13.5px] outline-none focus:border-[#7C4DFF]";
  const officerRow = (label, val, setVal, testid) => (
    <div className="rounded-xl p-3" style={{ background: "#F7F7FB" }}>
      <p className="mb-2 text-[13px] font-bold" style={{ color: C.ink }}>{label}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <input data-testid={`${testid}-name`} className={inp} style={{ borderColor: C.line }} placeholder="Name" value={val.name} onChange={(e) => setVal({ ...val, name: e.target.value })} />
        <input data-testid={`${testid}-mid`} className={inp} style={{ borderColor: C.line }} placeholder="Member ID" value={val.member_id} onChange={(e) => setVal({ ...val, member_id: e.target.value })} />
        <input data-testid={`${testid}-phone`} className={inp} style={{ borderColor: C.line }} placeholder="Phone" value={val.phone} onChange={(e) => setVal({ ...val, phone: e.target.value })} />
        <input data-testid={`${testid}-email`} className={inp} style={{ borderColor: C.line }} placeholder="Email" value={val.email} onChange={(e) => setVal({ ...val, email: e.target.value })} />
      </div>
    </div>
  );
  const save = async () => {
    if (!f.club_name.trim()) { toast.error("Club name is required"); return; }
    if (!f.ag_email || !ag) { toast.error("Pick an Assistant Governor"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/support/districts/${districtId}/clubs`, {
        club_name: f.club_name, rotary_club_id: f.rotary_club_id, test_club: false,
        zone_id: ag.zone_id, ag: { email: ag.email, name: ag.name },
        president: { name: pres.name, email: pres.email, phone: pres.phone, member_id: pres.member_id },
        secretary: { name: sec.name, email: sec.email, phone: sec.phone, member_id: sec.member_id },
      });
      const newId = r.data.club_id;
      if (file && newId) {
        const fd = new FormData(); fd.append("file", file);
        const mr = await api.post(`/support/districts/${districtId}/clubs/${newId}/members/bulk`, fd);
        toast.success(`"${f.club_name}" added · ${mr.data.created} member(s) imported`);
      } else {
        toast.success(`"${f.club_name}" added`);
      }
      onAdded && onAdded(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add club"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-club-dialog">
        <DialogHeader>
          <DialogTitle>Add new club</DialogTitle>
          <DialogDescription>The club is placed in the chosen AG's zone; Zone Governor &amp; Secretary carry over. Member list is optional — the President can upload it later from the club dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <input data-testid="add-club-name" className={inp} style={{ borderColor: C.line }} placeholder="Club name *" value={f.club_name} onChange={(e) => setF({ ...f, club_name: e.target.value })} />
            <input data-testid="add-club-id" className={inp} style={{ borderColor: C.line }} placeholder="Club ID (optional)" value={f.rotary_club_id} onChange={(e) => setF({ ...f, rotary_club_id: e.target.value })} />
          </div>
          {officerRow("President", pres, setPres, "add-pres")}
          {officerRow("Secretary", sec, setSec, "add-sec")}
          <div>
            <p className="mb-1.5 text-[13px] font-bold" style={{ color: C.ink }}>Assistant Governor *</p>
            <select data-testid="add-club-ag" className={inp} style={{ borderColor: C.line }} value={f.ag_email} disabled={noAg} onChange={(e) => setF({ ...f, ag_email: e.target.value })}>
              <option value="">{noAg ? "No Assistant Governors available" : "Select Assistant Governor *"}</option>
              {ags.map((a, i) => <option key={i} value={a.email}>{a.name} — {a.zone_name}</option>)}
            </select>
            {ag && agZone && (
              <div className="mt-2 rounded-xl p-3" style={{ background: "#F1FAF4" }} data-testid="add-club-zone-officials">
                <p className="text-[12.5px] font-bold" style={{ color: "#1E7A46" }}>Placed in {ag.zone_name}. These district officials carry over:</p>
                <ul className="mt-1 space-y-0.5 text-[12.5px]" style={{ color: C.ink }}>
                  <li><b>Zone Governor:</b> {agZone.zone_governor?.name || agZone.zone_governor?.email || "—"}</li>
                  <li><b>Zone Secretary:</b> {agZone.zone_secretary?.name || agZone.zone_secretary?.email || "—"}</li>
                  <li><b>Assistant Governor{(agZone.assistant_governors || []).length > 1 ? "s" : ""} in this zone:</b> {(agZone.assistant_governors || []).map((a) => a.name || a.email).join(", ") || "—"}</li>
                </ul>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-dashed p-3" style={{ borderColor: C.line }}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[13px] font-bold" style={{ color: C.ink }}>Member list <span className="font-normal" style={{ color: C.sub }}>(optional)</span></p>
              <button data-testid="add-club-members-template" onClick={() => window.open(`${api.defaults.baseURL}/support/districts/${districtId}/clubs/members-template`, "_blank")}
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: C.azure }}><DownloadSimple size={13} weight="bold" /> Sample</button>
            </div>
            <p className="mb-2 mt-0.5 text-[12px]" style={{ color: C.sub }}>Columns: Member ID, Full name, Address, Phone, Email.</p>
            <label data-testid="add-club-members-file" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: C.azure }}>
              <UploadSimple size={14} weight="bold" /> {file ? file.name : "Choose .xlsx"}
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
            </label>
          </div>
        </div>
        <DialogFooter>
          <GhostButton data-testid="add-club-cancel" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="add-club-save" onClick={save} disabled={busy || noAg}>{busy ? "Saving…" : "Save club"}</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---- Unresolved & Unassigned report -------------------------------------
// Read-only report of everything that could not be mapped, plus structural gaps in the
// activated data (clubs with no zone / no officers). Nothing is cleaned up or deleted — this
// only NAMES the orphans so they can be resolved later, and offers an in-app Assign action.
function AssignZoneDialog({ open, onClose, club, zones = [], onSaved }) {
  const [zoneId, setZoneId] = useState("");
  const [newZone, setNewZone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setZoneId(""); setNewZone(""); } }, [open]);
  if (!club) return null;
  const save = async () => {
    const body = { club_id: club.club_id };
    if (newZone.trim()) body.zone_name = newZone.trim();
    else if (zoneId) body.zone_id = zoneId;
    else { toast.error("Pick a zone or type a new one"); return; }
    setBusy(true);
    try {
      await api.post("/district-setup/assign", body);
      toast.success(`${club.club_name} placed in its zone`); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Set the zone for “{club.club_name}”</DialogTitle>
          <DialogDescription>Zone is a reporting label that groups clubs — it does not decide who is assigned. Officials are set per club under “Resolve”.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="mb-1.5 text-[13px] font-semibold" style={{ color: C.ink }}>Pick an existing zone</p>
            <select data-testid="assign-zone-select" value={zoneId} disabled={!!newZone.trim()} onChange={(e) => setZoneId(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-[14px]" style={{ borderColor: C.line, color: C.ink }}>
              <option value="">{zones.length ? "— Select a zone —" : "No zones yet — add one below"}</option>
              {zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.name}</option>)}
            </select>
          </div>
          <div>
            <p className="mb-1.5 text-[13px] font-semibold" style={{ color: C.ink }}>…or create a new zone</p>
            <input data-testid="assign-zone-new" value={newZone} onChange={(e) => setNewZone(e.target.value)} placeholder="New zone name (e.g. Zone H)"
              className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
          </div>
        </div>
        <DialogFooter>
          <GhostButton data-testid="assign-cancel" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="assign-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Set zone"}</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveOfficerDialog({ open, target, districtId, roleOptions = [], onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [manual, setManual] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [memberId, setMemberId] = useState("");
  const [role, setRole] = useState("president");
  const [busy, setBusy] = useState(false);
  const [holders, setHolders] = useState({ ags: [], holders: {} });
  const [pickedUid, setPickedUid] = useState("");
  const club = target?.club;
  const isOfficer = role === "president" || role === "secretary";
  const isAG = role === "Assistant Governor";
  const roleList = isAG ? (holders.ags || []) : (holders.holders?.[role] || []);
  useEffect(() => {
    if (open && club) {
      setData(null); setQ(""); setSel(null); setManual(false); setEmail(""); setMemberId("");
      const p = target?.person || "";
      setName(/^(member id|phone)\b/i.test(p) ? "" : p);
      setRole(target?.role || "president");
      setPickedUid("");
      api.get("/district-setup/role-holders").then((r) => setHolders(r.data || { ags: [], holders: {} })).catch(() => {});
      api.get(`/support/districts/${districtId}/clubs/${club.club_id}/members`)
        .then((r) => setData(r.data)).catch(() => setData({ members: [] }));
    }
  }, [open, club, districtId, target]);
  if (!club) return null;
  const members = data?.members || [];
  const matches = q.trim() ? members.filter((m) => (m.full_name || "").toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8) : [];
  const roleLabel = isOfficer ? (role === "president" ? "President" : "Secretary") : role;
  const save = async () => {
    // AG-first: picking an AG auto-maps the club's zone, ZG, ZS and that AG.
    if (isAG && pickedUid) {
      setBusy(true);
      try {
        const r = await api.post("/district-setup/assign-club-zone", { club_id: club.club_id, ag_user_id: pickedUid });
        toast.success(`AG ${r.data?.ag?.name || "set"} · zone ${r.data?.zone_name || ""} · ZG/ZS mapped automatically`);
        onSaved(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || "Could not map from AG"); }
      setBusy(false);
      return;
    }
    // Other non-officer roles: a picked holder fills name+email below.
    let body;
    if (isOfficer && !manual) {
      if (!sel) { toast.error("Pick a member or add them manually"); return; }
      body = { club_id: club.club_id, role, member_id: sel.member_id };
    } else if (isOfficer) {
      if (!name.trim() || !email.includes("@")) { toast.error("Enter a name and a valid email"); return; }
      body = { club_id: club.club_id, role, name: name.trim(), email: email.trim() };
    } else {
      if (!memberId.trim() && !email.includes("@")) { toast.error("Pick a role holder, or enter their email/Member ID"); return; }
      body = { club_id: club.club_id, role, name: name.trim(), email: email.trim(), member_id: memberId.trim() };
    }
    setBusy(true);
    try {
      const r = await api.post("/district-setup/resolve-officer", body);
      toast.success(`${roleLabel} set to ${r.data.member_name || "official"}${r.data.scope === "district" ? " (applies district-wide)" : ""}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resolve"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="resolve-officer-dialog">
        <DialogHeader>
          <DialogTitle>Configure {club.club_name}</DialogTitle>
          <DialogDescription>Fill any missing officer or district official for this club. Roles are assigned per club; a district-wide role (e.g. Treasurer) applies to every club.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select data-testid="resolve-role" value={role} onChange={(e) => { setRole(e.target.value); setSel(null); setManual(false); }}
            className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none" style={{ borderColor: C.line, color: C.ink }}>
            <option value="president">President</option>
            <option value="secretary">Secretary</option>
            {roleOptions.map((r) => <option key={r.label} value={r.label}>{r.label}{r.granularity === "district" ? " (district-wide)" : ""}</option>)}
          </select>
          {isOfficer && !data ? <Loader label="Loading members…" /> : isOfficer && !manual ? (
            <>
              <input data-testid="resolve-search" autoFocus value={sel ? (sel.full_name || sel.member_id) : q}
                onChange={(e) => { setSel(null); setQ(e.target.value); }} placeholder="Type a member's name…"
                className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              {!sel && q.trim() && (
                <div className="max-h-56 overflow-auto rounded-lg border" style={{ borderColor: C.line }} data-testid="resolve-matches">
                  {matches.length ? matches.map((m) => (
                    <button key={m.member_id} data-testid={`resolve-option-${m.member_id}`} onClick={() => { setSel(m); setQ(""); }}
                      className="block w-full px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-[#F5F3FF]" style={{ color: C.ink }}>{m.full_name || m.member_id}</button>
                  )) : <p className="px-3 py-2 text-[13px]" style={{ color: C.sub }}>No members match “{q.trim()}”.</p>}
                </div>
              )}
              <button data-testid="resolve-toggle-manual" onClick={() => setManual(true)} className="text-[12.5px] font-semibold" style={{ color: C.azure }}>+ Person isn't on the roster — add manually</button>
            </>
          ) : (
            <div className="space-y-2">
              {!isOfficer && roleList.length > 0 && (
                <div>
                  <select data-testid="resolve-holder-pick" value={pickedUid}
                    onChange={(e) => { const uid = e.target.value; setPickedUid(uid); const per = roleList.find((x) => x.user_id === uid); if (per && !isAG) { setName(per.name || ""); setEmail(per.email || ""); setMemberId(""); } }}
                    className="w-full rounded-lg border px-3 py-2 text-[14px]" style={{ borderColor: C.line, color: C.ink }}>
                    <option value="">— Pick from {isAG ? "Assistant Governors" : "existing " + role} —</option>
                    {roleList.map((x) => <option key={x.user_id} value={x.user_id}>{x.name}{isAG && x.zone_name ? " — " + x.zone_name : ""}</option>)}
                  </select>
                  {isAG && pickedUid && <p className="mt-1.5 text-[12px] font-semibold" style={{ color: "#1E7A46" }}>Zone, Zone Governor &amp; Zone Secretary will be mapped automatically.</p>}
                  {isAG && !pickedUid && <p className="mt-1.5 text-[12px]" style={{ color: C.sub }}>Pick the AG — the club's zone, ZG and ZS are then set automatically.</p>}
                </div>
              )}
              {isAG && roleList.length === 0 && <p className="text-[13px]" style={{ color: "#8A5A00" }}>No AGs configured yet — set them under “Zone officials” in the Allocation Manager first.</p>}
              {!isAG && (
                <>
                  {!isOfficer && roleList.length > 0 && <p className="pt-1 text-[12px] font-semibold" style={{ color: C.sub }}>…or add someone new:</p>}
                  <input data-testid="resolve-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
                    className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none" style={{ borderColor: C.line, color: C.ink }} />
                  <input data-testid="resolve-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={isOfficer ? "Email (their login)" : "Email — or leave blank if giving a Member ID"}
                    className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none" style={{ borderColor: C.line, color: C.ink }} />
                  {!isOfficer && <input data-testid="resolve-memberid" value={memberId} onChange={(e) => setMemberId(e.target.value)} placeholder="Member ID (optional — resolves their email)"
                    className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none" style={{ borderColor: C.line, color: C.ink }} />}
                  {isOfficer && <button data-testid="resolve-toggle-pick" onClick={() => setManual(false)} className="text-[12.5px] font-semibold" style={{ color: C.azure }}>← Pick an existing member instead</button>}
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <GhostButton data-testid="resolve-cancel" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="resolve-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Confirm & save"}</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnresolvedReport() {
  const [data, setData] = useState(null);
  const [assignClub, setAssignClub] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [districtId, setDistrictId] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const load = async () => {
    const p = await api.get("/me/principal").then((r) => r.data).catch(() => ({}));
    if (p.district_id) {
      setDistrictId(p.district_id);
      api.get("/district-setup/role-schema").then((r) => setRoleOptions(r.data.roles || [])).catch(() => setRoleOptions([]));
    }
    return api.get("/district-setup/unresolved").then((r) => setData(r.data)).catch(() => setData({ total: 0, sections: [], orphans: {}, zones: [] }));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const orphans = data?.orphans || {};
  const zones = data?.zones || [];

  // Consolidated per-club view: a club appears once and lists EVERY unresolved item
  // (Zone / President / Secretary / AG / ZG). One club-zone assignment fixes zone+AG+ZG together.
  const CLUB_ITEMS = [
    ["unzoned_clubs", "Zone", "zone"],
    ["clubs_missing_president", "President", "president"],
    ["clubs_missing_secretary", "Secretary", "secretary"],
    ["clubs_missing_ag", "Assistant Governor", "zone"],
    ["clubs_missing_zg", "Zone Governor", "zone"],
  ];
  const clubMap = new Map();
  CLUB_ITEMS.forEach(([key, label, action]) => {
    (orphans[key] || []).forEach((c) => {
      const e = clubMap.get(c.club_id) || { club_id: c.club_id, club_name: c.club_name, missing: [] };
      e.missing.push({ label, action, reason: c.reason });
      clubMap.set(c.club_id, e);
    });
  });
  const incompleteClubs = Array.from(clubMap.values()).sort((a, b) => a.club_name.localeCompare(b.club_name));
  const officialsMissing = orphans.officials_missing_email || [];

  return (
    <Card id="unresolved-report" className="border-2" style={{ borderColor: "#7C4DFF" }}>
      <SectionTitle icon={WarningCircle} title="Unresolved & unassigned"
        sub="Any club missing its Zone, President, Secretary or Assistant Governor is listed here until fully set up"
        right={<button data-testid="unresolved-refresh" onClick={() => { setData(null); load(); }} className="inline-flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: C.azure }}><ArrowClockwise size={14} weight="bold" /> Refresh</button>} />
      {!data ? <Loader label="Checking…" /> : data.total === 0 ? (
        <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-3 text-[14px]" style={{ background: "#F1FAF4", color: "#1E7A46" }} data-testid="unresolved-empty">
          <CheckCircle size={18} weight="fill" /> Everything is mapped — no unresolved people or unassigned clubs.
        </div>
      ) : (
        <div className="mt-3 space-y-4" data-testid="unresolved-report">
          {/* Consolidated: clubs not fully set up (one card per club) */}
          {incompleteClubs.length > 0 && (
            <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line }} data-testid="orphan-clubs-incomplete">
              <p className="mb-1 inline-flex items-center gap-2 text-[14px] font-bold" style={{ color: C.ink }}>
                <WarningCircle size={16} weight="bold" style={{ color: "#8A5A00" }} /> Clubs not fully set up
                <span className="rounded-full px-2 py-0.5 text-[11.5px] font-bold" style={{ background: "#FFF2D6", color: "#8A5A00" }}>{incompleteClubs.length}</span>
              </p>
              <p className="mb-2 text-[12.5px]" style={{ color: C.sub }}>Each club stays here until its <b>Zone</b>, <b>President</b>, <b>Secretary</b> and <b>Assistant Governor</b> are all resolved. Setting the zone inherits the AG, Zone Governor &amp; Zone Secretary.</p>
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {incompleteClubs.map((c) => {
                  const needsZone = c.missing.some((m) => m.label === "Zone");
                  const needsAG = c.missing.some((m) => m.label === "Assistant Governor");
                  const needsZG = c.missing.some((m) => m.label === "Zone Governor");
                  const needsPres = c.missing.some((m) => m.label === "President");
                  const needsSec = c.missing.some((m) => m.label === "Secretary");
                  return (
                    <div key={c.club_id} className="flex flex-col gap-2 rounded-xl px-3 py-2.5" style={{ background: "#F7F7FB" }} data-testid={`orphan-row-${c.club_id}`}>
                      <span className="block text-[13.5px] font-semibold leading-tight" style={{ color: C.ink }}>{c.club_name}</span>
                      <span className="flex flex-wrap gap-1">
                        {c.missing.map((m, mi) => (
                          <span key={mi} className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "#FDECEC", color: C.cranberry }} data-testid={`orphan-chip-${c.club_id}-${m.label.replace(/\s+/g, "-").toLowerCase()}`}>{m.label}</span>
                        ))}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {(needsZone || needsZG) && <button data-testid={`assign-btn-${c.club_id}`} onClick={() => setAssignClub(c)} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: C.azure }}>Set zone</button>}
                        {needsAG && <button data-testid={`resolve-btn-ag-${c.club_id}`} onClick={() => setResolveTarget({ club: c, role: "Assistant Governor" })} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: C.azure }}>Assistant Governor</button>}
                        {needsPres && <button data-testid={`resolve-btn-president-${c.club_id}`} onClick={() => setResolveTarget({ club: c, role: "president" })} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: C.azure }}>President</button>}
                        {needsSec && <button data-testid={`resolve-btn-secretary-${c.club_id}`} onClick={() => setResolveTarget({ club: c, role: "secretary" })} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: C.azure }}>Secretary</button>}
                        <button data-testid={`configure-btn-${c.club_id}`} onClick={() => setResolveTarget({ club: c, role: needsPres ? "president" : needsSec ? "secretary" : needsAG ? "Assistant Governor" : "president" })} title="Fill any officer or district official for this club"
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold" style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line, background: "#fff" }}>Configure</button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* District officials with no email (not club-scoped) */}
          {officialsMissing.length > 0 && (
            <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line }} data-testid="orphan-officials_missing_email">
              <p className="mb-1 inline-flex items-center gap-2 text-[14px] font-bold" style={{ color: C.ink }}>
                <UserPlus size={16} weight="bold" style={{ color: "#8A5A00" }} /> District officials with no email
                <span className="rounded-full px-2 py-0.5 text-[11.5px] font-bold" style={{ background: "#FFF2D6", color: "#8A5A00" }}>{officialsMissing.length}</span>
              </p>
              <p className="mb-2 text-[12.5px]" style={{ color: C.sub }}>These district officials were read from the allocation but have no email, so they were NOT onboarded. Add an email below and they'll get a login invite.</p>
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {officialsMissing.map((c, ri) => (
                  <div key={c.club_id + (c.role || "") + ri} className="flex items-start justify-between gap-2 rounded-xl px-3 py-2" style={{ background: "#F7F7FB" }} data-testid={`orphan-official-${c.club_id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-semibold leading-tight" style={{ color: C.ink }}>{c.club_name}</span>
                      {(c.role || c.person) && <span className="mt-0.5 block text-[12.5px] font-semibold leading-tight" style={{ color: C.indigo }}>{c.role}{c.person ? ` · ${c.person}` : ""}</span>}
                      {c.reason && <span className="mt-0.5 block text-[11.5px] leading-snug" style={{ color: "#8A5A00" }}>{c.reason}</span>}
                    </span>
                    <button data-testid={`resolve-btn-official-${c.club_id}`} onClick={() => setResolveTarget({ club: c, role: c.role, person: c.person })} className="inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: C.azure }}>Resolve</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Input warnings (from the stored workbooks), grouped by reason */}
          {(data.sections || []).map((s) => (
            <div key={s.key} className="rounded-2xl border p-3.5" style={{ borderColor: C.line }} data-testid={`unresolved-section-${s.key}`}>
              <p className="inline-flex items-center gap-2 text-[14px] font-bold" style={{ color: C.ink }}>
                {s.title} <span className="rounded-full px-2 py-0.5 text-[11.5px] font-bold" style={{ background: "#FFF2D6", color: "#8A5A00" }}>{s.items.length}</span>
              </p>
              {s.hint && <p className="mt-0.5 text-[12.5px]" style={{ color: C.sub }}>{s.hint}</p>}
              <ul className="mt-2 max-h-56 list-disc space-y-0.5 overflow-auto pl-5 text-[13px]" style={{ color: C.ink }}>
                {s.items.map((it, i) => <li key={i}>{it.text}{it.line ? <span style={{ color: C.sub }}> (row {it.line})</span> : null}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
      <AssignZoneDialog open={!!assignClub} club={assignClub} zones={zones}
        onClose={() => setAssignClub(null)} onSaved={() => { setData(null); load(); }} />
      <ResolveOfficerDialog open={!!resolveTarget} target={resolveTarget} districtId={districtId} roleOptions={roleOptions}
        onClose={() => setResolveTarget(null)} onSaved={() => { setData(null); load(); }} />
    </Card>
  );
}

function WipeDataCard() {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(false);
  const [repo, setRepo] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = () => { setYear(false); setRepo(false); setConfirm(""); };
  const canWipe = (year || repo) && confirm.trim() === "WIPE" && !busy;
  const run = async () => {
    if (!canWipe) return;
    setBusy(true);
    try {
      const r = await api.post("/district-setup/wipe", {
        wipe_year: year, wipe_repository: repo, confirm: confirm.trim(),
      });
      const rows = Object.entries(r.data.deleted || {}).reduce((a, [, v]) => a + v, 0);
      toast.success(`Wiped ${rows} record(s) and ${r.data.files_deleted} file(s). Reloading…`);
      setOpen(false); reset();
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Wipe failed"); }
    setBusy(false);
  };
  const Row = ({ checked, onChange, testid, title, sub }) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border p-3" style={{ borderColor: C.line }}>
      <Checkbox data-testid={testid} checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold" style={{ color: C.ink }}>{title}</span>
        <span className="block text-[12.5px]" style={{ color: C.sub }}>{sub}</span>
      </span>
    </label>
  );
  return (
    <Card data-testid="wipe-data-card" className="border-2" >
      <SectionTitle icon={Trash} title="Danger zone"
        sub="Permanently wipe this district / Rotary Year's data. This cannot be undone." />
      <button data-testid="wipe-open-btn" onClick={() => { reset(); setOpen(true); }}
        className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: C.cranberry }}>
        <Trash size={16} weight="bold" /> Wipe District-Year Data
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent data-testid="wipe-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: C.cranberry }}>
              <WarningCircle size={20} weight="fill" /> Wipe data — cannot be undone
            </DialogTitle>
            <DialogDescription>
              Choose exactly what to remove. Everything is strictly limited to your active district and Rotary Year.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Row checked={year} onChange={setYear} testid="wipe-year-checkbox"
              title="Wipe all clubs & roles for this year"
              sub="This Rotary Year's zones, officers, President/Secretary, role schema and all GOV working data + this year's uploaded files. Keeps the club & member roster." />
            <Row checked={repo} onChange={setRepo} testid="wipe-repo-checkbox"
              title="Wipe the entire Member Repository for this district"
              sub="All members and clubs for this district (across every year) and every photo/file in this district's storage silo." />
            <div>
              <label className="block text-[13px] font-semibold" style={{ color: C.ink }}>Type <b>WIPE</b> to confirm</label>
              <input data-testid="wipe-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="WIPE" className="mt-1 w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#F0577D]"
                style={{ borderColor: C.line, color: C.ink }} />
            </div>
          </div>
          <DialogFooter>
            <GhostButton data-testid="wipe-cancel-btn" onClick={() => { setOpen(false); reset(); }}>Cancel</GhostButton>
            <button data-testid="wipe-execute-btn" onClick={run} disabled={!canWipe}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-semibold text-white transition-opacity"
              style={{ background: C.cranberry, opacity: canWipe ? 1 : 0.5 }}>
              {busy ? "Wiping…" : "Wipe permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AddOfficialCard() {
  const [clubs, setClubs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scope, setScope] = useState("club");
  const [clubId, setClubId] = useState("");
  const [role, setRole] = useState("");
  const [memberId, setMemberId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/district-setup/clubs").then((r) => setClubs(r.data.clubs || [])).catch(() => setClubs([]));
    api.get("/district-setup/role-schema").then((r) => setRoles(r.data.roles || [])).catch(() => setRoles([]));
  }, []);
  const submit = async () => {
    if (!role.trim()) { toast.error("Enter or pick a role"); return; }
    if (scope === "club" && !clubId) { toast.error("Pick a club"); return; }
    if (!memberId.trim() && !email.includes("@")) { toast.error("Enter their email or a Member ID"); return; }
    setBusy(true);
    try {
      const r = await api.post("/district-setup/add-official", {
        role: role.trim(), granularity: scope, club_id: scope === "club" ? clubId : "",
        member_id: memberId.trim(), email: email.trim(), name: name.trim(),
      });
      toast.success(`${r.data.role} added${r.data.scope === "district" ? " (district-wide)" : ""}${r.data.added_role ? " — new role created" : ""}${r.data.invite === "failed" ? " · invite email failed" : " · invite sent"}`);
      setRole(""); setMemberId(""); setEmail(""); setName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add official"); }
    setBusy(false);
  };
  const inp = "w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]";
  return (
    <Card data-testid="add-official-card">
      <SectionTitle icon={UserPlus} title="Add a district official"
        sub="Assign any district official at any time — pick an existing role or type a new one. They get an email invite to log in." />
      <div className="mt-1 space-y-2">
        <div className="flex gap-2" data-testid="official-scope">
          {[["club", "A specific club"], ["district", "Whole district"]].map(([v, lbl]) => (
            <button key={v} data-testid={`official-scope-${v}`} onClick={() => setScope(v)}
              className="flex-1 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors"
              style={scope === v ? { background: C.indigo, color: "#fff" } : { boxShadow: "inset 0 0 0 1.5px " + C.line, color: C.ink }}>{lbl}</button>
          ))}
        </div>
        {scope === "club" && (
          <select data-testid="official-club" value={clubId} onChange={(e) => setClubId(e.target.value)} className={inp} style={{ borderColor: C.line, color: C.ink }}>
            <option value="">{clubs.length ? "— Select a club —" : "No clubs yet"}</option>
            {clubs.map((c) => <option key={c.club_id} value={c.club_id}>{c.club_name}</option>)}
          </select>
        )}
        <input data-testid="official-role" list="official-role-list" value={role} onChange={(e) => setRole(e.target.value)}
          placeholder="Role (e.g. Assistant Governor, or a new one)" className={inp} style={{ borderColor: C.line, color: C.ink }} />
        <datalist id="official-role-list">
          {roles.map((r) => <option key={r.label} value={r.label} />)}
        </datalist>
        <input data-testid="official-memberid" value={memberId} onChange={(e) => setMemberId(e.target.value)}
          placeholder="Member ID (resolves their email) — or use email below" className={inp} style={{ borderColor: C.line, color: C.ink }} />
        <input data-testid="official-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (their login)" className={inp} style={{ borderColor: C.line, color: C.ink }} />
        <input data-testid="official-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Full name (optional)" className={inp} style={{ borderColor: C.line, color: C.ink }} />
        <PrimaryButton data-testid="official-submit" className="w-full" onClick={submit} disabled={busy}>{busy ? "Adding…" : "Add official & send invite"}</PrimaryButton>
      </div>
    </Card>
  );
}

function AddCoAdminCard() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/district-setup/add-admin", { email: email.trim(), display_name: name.trim() });
      toast.success(r.data?.invite === "failed" ? "Admin added — but the invite email failed to send" : "Co-admin added — invite email sent");
      setEmail(""); setName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add admin"); }
    setBusy(false);
  };
  return (
    <Card data-testid="add-coadmin-card">
      <SectionTitle icon={UserPlus} title="District admins"
        sub="Add another District Admin for this district & year. They get an email invite to set up their own login — no Owner/Support access needed." />
      <div className="mt-1 space-y-2">
        <input data-testid="coadmin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.org"
          className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
        <input data-testid="coadmin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (if new)"
          className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
        <PrimaryButton data-testid="coadmin-submit" className="w-full" onClick={submit} disabled={busy}>{busy ? "Sending…" : "Send admin invite"}</PrimaryButton>
      </div>
    </Card>
  );
}

function DistrictOfficerInvitesCard() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [resending, setResending] = useState(null);
  const loadStatus = async () => {
    try { const r = await api.get("/admin/district-officers-status"); setStatus(r.data); }
    catch { /* non-blocking */ }
  };
  useEffect(() => { loadStatus(); }, []);
  const inviteAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/invite-district-officers");
      const wa = r.data.whatsapp_configured ? ` · WhatsApp ${r.data.whatsapp_sent}` : "";
      toast.success(`Invited ${r.data.officers} officer${r.data.officers === 1 ? "" : "s"} · ${r.data.emailed} emailed${wa}`);
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invitations"); }
    finally { setBusy(false); }
  };
  const resend = async (o) => {
    setResending(o.user_id);
    try {
      await api.post("/admin/invite-district-officers", { user_id: o.user_id });
      toast.success(`Link re-sent to ${o.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setResending(null); }
  };
  const BADGE = {
    activated: { label: "Activated", bg: "#E7F6EC", fg: "#1E7A46" },
    invited: { label: "Invited · pending", bg: "#FFF4E5", fg: "#B5730A" },
    no_email: { label: "No email", bg: "#FDECEC", fg: "#C8102E" },
  };
  return (
    <Card className="border-2" style={{ borderColor: "#17458F" }}>
      <SectionTitle icon={UserPlus} title="Onboard district officers" />
      <p className="mt-1 text-[13px]" style={{ color: C.sub }}>
        Email every district &amp; zone officer their personal, secure link to set a password and reach
        their dashboard. Already-active officers are skipped, and the same link is re-used on every send.
      </p>
      <PrimaryButton data-testid="invite-district-officers-btn" onClick={inviteAll} disabled={busy} className="mt-3">
        {busy ? "Sending…" : "Invite all district officers"}
      </PrimaryButton>

      {status && (
        <div className="mt-4" data-testid="officer-onboarding-status">
          <div className="mb-2 flex flex-wrap gap-2 text-[12.5px] font-semibold">
            <span className="rounded-full px-2.5 py-1" style={{ background: "#E7F6EC", color: "#1E7A46" }}>{status.activated} activated</span>
            <span className="rounded-full px-2.5 py-1" style={{ background: "#FFF4E5", color: "#B5730A" }}>{status.pending} pending</span>
            {status.no_email > 0 && <span className="rounded-full px-2.5 py-1" style={{ background: "#FDECEC", color: "#C8102E" }}>{status.no_email} no email</span>}
          </div>
          <div className="max-h-[340px] divide-y overflow-y-auto rounded-xl border" style={{ borderColor: C.line }}>
            {status.officers.map((o) => {
              const b = BADGE[o.status] || BADGE.invited;
              return (
                <div key={o.user_id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`officer-row-${o.user_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-[13.5px] font-semibold" style={{ color: C.ink }}>{o.name}</p>
                    <p className="truncate text-[12px]" style={{ color: C.sub }}>{o.role || "Officer"}{o.email ? ` · ${o.email}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[11.5px] font-semibold" style={{ background: b.bg, color: b.fg }}>{b.label}</span>
                    {o.status === "invited" && (
                      <button data-testid={`resend-officer-${o.user_id}`} onClick={() => resend(o)} disabled={resending === o.user_id}
                        className="rounded-lg px-2.5 py-1 text-[12px] font-semibold" style={{ background: C.indigoSoft || "#EEF1FF", color: C.indigoText || "#17458F" }}>
                        {resending === o.user_id ? "…" : "Resend"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!status.officers.length && <p className="mt-2 text-[13px]" style={{ color: C.sub }}>No district officers configured yet.</p>}
        </div>
      )}
    </Card>
  );
}

export default function SetupYear() {
  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <PageHero title="District GOV settings" subtitle="Set up the Rotary Year and manage the district — you can update anything later." />
      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="space-y-6 xl:flex-1 min-w-0">
          <RequirementsSection />
          <ClubsSection />
          <GovScheduleSection />
          <UnresolvedReport />
          <RoleAccessSection />
        </div>
        <div className="space-y-6 xl:w-[360px] xl:shrink-0">
          <MembersSection />
          <FoundationGivingSection />
          <AddOfficialCard />
          <DistrictOfficerInvitesCard />
          <AddCoAdminCard />
          <WipeDataCard />
          <DirectoryCoverSection />
          <AddClubCard />
          <Card className="border-2" style={{ borderColor: "#7C4DFF" }}>
            <button data-testid="view-unresolved-report-btn"
              onClick={() => document.getElementById("unresolved-report")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="flex w-full items-center justify-center gap-2 text-[14px] font-bold" style={{ color: C.indigo }}>
              <WarningCircle size={18} weight="bold" /> View unassigned/unmapped report
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}
